import * as React from 'react';
import PropTypes from 'prop-types';
import { useRef, useCallback, useState } from 'react';
import { isEqual } from 'radash';
import {
  Tldraw,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultSizeStyle,
  DefaultHorizontalAlignStyle,
  DefaultVerticalAlignStyle,
  InstancePresenceRecordType,
  setDefaultUiAssetUrls,
  setDefaultEditorAssetUrls,
  toolbarItem,
  GeoShapeGeoStyle,
  useEditor,
  useValue,
  stopEventPropagation,
} from '@bigbluebutton/tldraw';
import '@bigbluebutton/tldraw/tldraw.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import { compressToBase64, decompressFromBase64 } from 'lz-string';
import SlideCalcUtil, { HUNDRED_PERCENT } from '/imports/utils/slideCalcUtils';
import meetingClientSettingsInitialValues from '/imports/ui/core/initial-values/meetingClientSettings';
import getFromUserSettings from '/imports/ui/services/users-settings';
import KEY_CODES from '/imports/utils/keyCodes';
import { debounce } from '/imports/utils/debounce';
import logger from '/imports/startup/client/logger';
import Styled from './styles';
import {
  mapLanguage,
  isValidShapeType,
  usePrevious,
  getDifferences,
} from './utils';
import { useMouseEvents, useCursor } from './hooks';
import { notifyShapeNumberExceeded, notifyShapeNumberWarning, getCustomEditorAssetUrls, getCustomAssetUrls, handleFiledrop, handleSavePresentation, compressImage } from './service';
import NoopTool from './custom-tools/noop-tool/component';
import DeleteAllTool from "./custom-tools/delete-all/component";
import GridTool from "./custom-tools/grid-tool/component";
import { SVGShapeUtil } from './custom-shapes/CardShapeUtil';
import { GraphGridShapeUtil } from './custom-shapes/GridShapeUtil';
import { courseSelect } from '../app/context';
import update from 'immutability-helper';
import useDeduplicatedSubscription from '../../core/hooks/useDeduplicatedSubscription';
import { PRESENTATIONS_SUBSCRIPTION } from './queries';
import { useMutation } from '@apollo/client';
import { PRESENTATION_REMOVE, PRESENTATION_SET_CURRENT } from '../presentation/mutations';
import { useIsPresentationEnabled } from '../../services/features';
import { notify } from '../../services/notification';
import { DropEvents } from './DropEvents/component';
import { SMALL_VIEWPORT_BREAKPOINT } from '../layout/enums';
import { layoutSelectInput } from '../layout/context';
import { isDesktop } from '../layout/utils';
import { normalizePlusString } from '../nav-bar/nav-bar-graphql/talking-indicator/service';
import AppService, { insertSlideData } from '/imports/ui/components/app/service';
import { toPng, toSvg } from 'html-to-image';
import LoadingScreen from '../common/loading-screen/component';
const CAMERA_TYPE = 'camera';

const shapeUtils = [SVGShapeUtil, GraphGridShapeUtil];

// Helper functions
const deleteLocalStorageItemsWithPrefix = (prefix) => {
  const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(prefix));
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

// Example of typical LocalStorage entry tldraw creates:
// `{ TLDRAW_USER_DATA_v3: '{"version":2,"user":{"id":"epDk1 ...`
const clearTldrawCache = () => {
  deleteLocalStorageItemsWithPrefix('TLDRAW');
};

const createCamera = (pageId, zoomLevel) => ({
  id: `camera:page:${pageId}`,
  meta: {},
  typeName: CAMERA_TYPE,
  x: 0,
  y: 0,
  z: zoomLevel,
});
const createLookup = (arr) =>
  arr.reduce((acc, entry) => {
    acc[entry.id] = entry;
    return acc;
  }, {});

const defaultUser = {
  userId: '',
};

const customTools = [NoopTool, DeleteAllTool, GridTool];

const Whiteboard = React.memo((props) => {
  const {
    isPresenter = false,
    removeShapes,
    persistShapeWrapper,
    shapes,
    removedShapes,
    assets,
    currentUser = defaultUser,
    whiteboardId = undefined,
    zoomSlide,
    curPageNum: curPageId,
    zoomChanger,
    isMultiUserActive,
    isRTL,
    fitToWidth,
    zoomValue,
    colorStyle,
    dashStyle,
    fillStyle,
    fontStyle,
    sizeStyle,
    presentationAreaHeight,
    presentationAreaWidth,
    setTldrawIsMounting,
    setTldrawAPI,
    whiteboardToolbarAutoHide,
    toggleToolsAnimations,
    animations,
    isToolbarVisible,
    isModerator,
    currentPresentationPage,
    presentationId = undefined,
    hasWBAccess,
    bgShape,
    publishCursorUpdate,
    otherCursors,
    hideViewersCursor,
    presentationWidth,
    presentationHeight,
    skipToSlide,
    intl,
    maxNumberOfAnnotations,
    notifyNotAllowedChange,
    locale,
    darkTheme,
    selectedLayout,
    isInfiniteWhiteboard,
    whiteboardWriters,
    isPhone,
    setEditor,
    isManualSyncTriggeredRef,
    rectShape,
    presentationName = '',
    setIsPdfLoading
  } = props;

  clearTldrawCache();
  const PRESENTATION_CONFIG = window.meetingClientSettings.public.presentation;
  const [isMounting, setIsMounting] = React.useState(true);
  const [cursorType, setCursorType] = React.useState('');
  if (isMounting) {
    setDefaultEditorAssetUrls(getCustomEditorAssetUrls());
    setDefaultUiAssetUrls(getCustomAssetUrls());
  }

  const whiteboardRef = React.useRef(null);
  const zoomValueRef = React.useRef(null);
  const prevShapesRef = React.useRef(shapes);
  const tlEditorRef = React.useRef(null);
  const slideChanged = React.useRef(false);
  const slideNext = React.useRef(null);
  const prevZoomValueRef = React.useRef(null);
  const initialZoomRef = useRef(null);
  const isMouseDownRef = useRef(false);
  const shapeBatchRef = useRef({});
  const isMountedRef = useRef(false);
  const isPresenterRef = useRef(isPresenter);
  const fitToWidthRef = useRef(fitToWidth);
  const whiteboardIdRef = React.useRef(whiteboardId);
  const curPageIdRef = React.useRef(curPageId);
  const hasWBAccessRef = React.useRef(hasWBAccess);
  const isModeratorRef = React.useRef(isModerator);
  const currentPresentationPageRef = React.useRef(currentPresentationPage);
  const initialViewBoxWidthRef = React.useRef(null);
  const initialViewBoxHeightRef = React.useRef(null);
  const previousTool = React.useRef(null);
  const bgSelectedRef = React.useRef(false);
  const boundarySelectedRef = React.useRef(false);
  const lastVisibilityStateRef = React.useRef('');
  const mountedTimeoutIdRef = useRef(null);
  const presentationIdRef = React.useRef(presentationId);
  const innerWrapperPollingFrameRef = React.useRef(null);
  const isMountedPollingFrameRef = React.useRef(null);
  const isWheelZoomRef = React.useRef(false);
  const [pageZoomMap, setPageZoomMap] = useState(() => {
    try {
      const saved = localStorage.getItem('pageZoomMap');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [isFirstTimeCentering, setIsFirstTimeCentering] = useState({});
  const courseDetails = courseSelect((state) => state);

  const courseName = courseDetails?.course?.courseDetail?.courseName

  const lessonName = courseDetails?.course?.courseDetail?.lesson


  const prepareLesson = courseDetails?.course?.courseDetail?.isPrepareLesson ?? false

  // Debug function to track first-time centering state
  const logFirstTimeCenteringState = (slideId, presentationId) => {
    const slideKey = `${presentationId}-${slideId}`;
    const isFirstTime = !isFirstTimeCentering[slideKey];
    // console.log('First-time centering state:', {
    //   slideKey,
    //   slideId,
    //   presentationId,
    //   isFirstTime,
    //   allFlags: isFirstTimeCentering
    // });
    return isFirstTime;
  };



  const handleAssetSelection = async (editor, tools) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async () => {
        const src = reader.result;
        if (!src) return;

        // Always show a text debug shape (so you see something no matter what)
        // editor.createShape({
        //   type: 'text',
        //   x: 200,
        //   y: 200,
        //   props: {
        //     text: typeof src === 'string' ? src.slice(0, 40) + '...' : 'Invalid src',
        //     font: 'draw',
        //     size: 'm',
        //     color: 'black',
        //     align: 'start',
        //     autoSize: true,
        //   },
        // });

        // Wrap image loading in a Promise
        const loadImage = (src) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
          });

        try {
          const img = await loadImage(src);
          const { width, height } = img;

          const maxSize = 300;
          const scaleUpFactor = 2; // ⬅️ make image 20% larger after resize

          let w = width;
          let h = height;

          if (w > h && w > maxSize) {
            const scale = maxSize / w;
            w = Math.round(maxSize * scaleUpFactor);
            h = Math.round(h * scale * scaleUpFactor);
          } else if (h >= w && h > maxSize) {
            const scale = maxSize / h;
            h = Math.round(maxSize * scaleUpFactor);
            w = Math.round(w * scale * scaleUpFactor);
          }

          const bounds = editor.getViewportPageBounds();
          let point = { x: 100, y: 100 };

          if (bounds) {
            const padding = 150;
            const minX = bounds.minX + padding;
            const maxX = bounds.maxX - padding;
            const minY = bounds.minY + padding;
            const maxY = bounds.maxY - padding;

            const randomX = minX + Math.random() * (maxX - minX);
            const randomY = minY + Math.random() * (maxY - minY);

            point = { x: randomX, y: randomY };

            if (editor.getShapesAtPoint(point).length > 0) {
              point = { x: point.x + 30, y: point.y + 30 };
            }
          }
          // const { compressedSrc, mimeType } = await compressImage(img, w, h, file.type,file.size, src);
          const { compressedSrc, mimeType } = await compressImage(src, file.type, file.size);
          const assetId = `asset:${Date.now()}`;

          await editor.createAssets([
            {
              id: assetId,
              typeName: 'asset',
              type: 'image',
              props: {
                src: compressedSrc,
                w,
                h,
                name: file.name,
                mimeType: file.type,
                isAnimated: false,
              },
              meta: {},
            },
          ]);
          // Delay shape creation so asset is synced

          if (editor.store.has(assetId)) {
            editor.createShape({
              id: `shape:${Date.now()}`,
              type: "image",
              x: point.x ?? 100,
              y: point.y ?? 100,
              props: { assetId, w, h },
            });

            editor.setCurrentTool("select");
          }
          //    setTimeout(() => {
          // }, 500); // t

        } catch (err) {
          console.error('Image load failed', err);
          // fallback: create a red error text
          // editor.createShape({
          //   type: 'text',
          //   x: 200,
          //   y: 240,
          //   props: {
          //     text: '❌ Failed to load image',
          //     font: 'draw',
          //     size: 'm',
          //     color: 'red',
          //     align: 'start',
          //     autoSize: true,
          //   },
          // });
        }
      };

      reader.readAsDataURL(file);
    };

    input.click();
  };

  const customUiOverrides = React.useMemo(() => ({
    tools: (editor, tools) => {
      const updatedTools = {
        ...tools,
        deleteAll: {
          id: "delete-all",
          label: intl?.messages["app.whiteboard.toolbar.clear"],
          readonlyOk: false,
          icon: "tool-delete-all",
          onSelect() {
            editor.deleteShapes(editor.getCurrentPageShapes().map(shape => {
              if (currentUser?.presenter || (shape?.meta?.createdBy === currentUser?.userId)) {
                return shape.id;
              }
              return '';
            })?.filter(s => s?.length > 0));
          },
        },
        asset: {
          ...tools.asset,
          onSelect: async () => {
            handleAssetSelection(editor)
          },
        },
      }
      // Remove the text tool
      // delete tools.actions
      return updatedTools;
    },
    toolbar: (_editor, toolbarItems, { tools }) => {

      return toolbarItems
        .filter((item) => {
          // Remove geo tools from bottom area (recent section)
          if (item.id.startsWith('geo-') && item.group === 'group') return false
          return true
        })
        .map((item) =>
          item.id.startsWith('geo-')
            ? { ...item, group: 'extra' } // Put all geo tools into More Tools
            : item
        )
      // return toolbarItems;
      // if (tools.asset) {
      //   toolbarItems.splice(8, 0, toolbarItem(tools.asset));
      // }
      // // if (tools.deleteAll) {
      // //   toolbarItems.splice(7, 0, toolbarItem(tools.deleteAll));
      // // }
      // // if (tools.gridTool) {  // Add the new tool to the toolbar
      // //   toolbarItems.splice(8, 0, toolbarItem(tools.gridTool));
      // // }
      // return toolbarItems;
    },
  }), [intl, currentUser?.presenter, currentUser?.userId]);

  const prevFitToWidth = usePrevious(fitToWidth);
  const presenterChanged = usePrevious(isPresenter) !== isPresenter;
  const pageChanged = usePrevious(curPageId) !== curPageId;

  let clipboardContent = null;
  let isPasting = false;
  let pasteTimeout = null;

  const setIsMouseDown = (val) => {
    isMouseDownRef.current = val;
  };

  const sanitizeShape = (shape) => {
    const { isModerator, questionType, ...rest } = shape;
    return {
      ...rest,
    };
  };

  const debouncedUpdateShapes = debounce(() => {
    if (shapes && Object.keys(shapes).length > 0) {
      prevShapesRef.current = shapes;
      tlEditorRef.current?.store.mergeRemoteChanges(() => {
        const remoteShapesArray = Object.values(prevShapesRef.current).reduce((acc, shape) => {
          if (shape.meta?.presentationId === presentationIdRef.current || shape?.whiteboardId?.includes(presentationIdRef.current)) {
            acc.push(sanitizeShape(shape));
          }
          return acc;
        }, []);

        if (pageChanged) {
          cleanupStore(`page:${parseInt(curPageIdRef.current, 10)}`);
          tlEditorRef.current?.store.put(assets);
          tlEditorRef.current?.store.put(bgShape);
          tlEditorRef.current?.store.put(rectShape)
        }
        tlEditorRef.current?.store.put(remoteShapesArray);
      });
    }
  }, 175);

  React.useEffect(() => {
    localStorage.setItem('pageZoomMap', JSON.stringify(pageZoomMap));
  }, [pageZoomMap]);

  React.useEffect(() => {
    currentPresentationPageRef.current = currentPresentationPage;
    // REMOVED ZOOM CONSTRAINTS - Allow complete freedom for presenters [FIRST]
    // if (tlEditorRef.current && currentPresentationPage) {
    //   updateZoomConstraints(tlEditorRef.current);
    // }
  }, [currentPresentationPage]);

  React.useEffect(() => {
    curPageIdRef.current = curPageId;
  }, [curPageId]);

  React.useEffect(() => {
    isModeratorRef.current = isModerator;
  }, [isModerator]);

  React.useEffect(() => {
    whiteboardIdRef.current = whiteboardId;
  }, [whiteboardId]);

  React.useEffect(() => {
    presentationIdRef.current = presentationId;
  }, [presentationId]);



  React.useEffect(() => {
    hasWBAccessRef.current = hasWBAccess;

    if (!hasWBAccess && !isPresenter) {
      tlEditorRef?.current?.setCurrentTool('noop');
    } else if (hasWBAccess && !isPresenter) {
      tlEditorRef?.current?.setCurrentTool('draw');
    }
  }, [hasWBAccess]);

  React.useEffect(() => {
    isPresenterRef.current = isPresenter;

    if (!hasWBAccessRef.current && !isPresenter) {
      tlEditorRef?.current?.setCurrentTool('noop');
    }
  }, [isPresenter]);

  React.useEffect(() => {
    fitToWidthRef.current = fitToWidth;
  }, [fitToWidth]);

  React.useEffect(() => {
    debouncedUpdateShapes();
  }, [shapes]);

  React.useEffect(() => {
    if (removedShapes && removedShapes.length > 0) {
      tlEditorRef.current?.store.remove([...removedShapes]);
    }
  }, [removedShapes]);

  const handleCopy = useCallback(() => {
    const selectedShapes = tlEditorRef.current?.getSelectedShapes();
    if (!selectedShapes || selectedShapes.length === 0) {
      return;
    }
    const content = tlEditorRef.current?.getContentFromCurrentPage(
      selectedShapes.map((shape) => shape.id),
    );
    if (content) {
      clipboardContent = content;
      const stringifiedClipboard = compressToBase64(
        JSON.stringify({
          type: 'application/tldraw',
          kind: 'content',
          data: content,
        }),
      );

      if (navigator.clipboard?.write) {
        const htmlBlob = new Blob([`<tldraw>${stringifiedClipboard}</tldraw>`], {
          type: 'text/html',
        });

        navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': new Blob([''], { type: 'text/plain' }),
          }),
        ]);
      } else if (navigator.clipboard.writeText) {
        navigator.clipboard.writeText(`<tldraw>${stringifiedClipboard}</tldraw>`);
      }
    }
  }, [tlEditorRef]);

  const handleCut = useCallback((shouldCopy) => {
    const selectedShapes = tlEditorRef.current?.getSelectedShapes();
    if (!selectedShapes || selectedShapes.length === 0) {
      return;
    }
    if (shouldCopy) {
      handleCopy();
    }
    tlEditorRef.current?.deleteShapes(selectedShapes.map((shape) => shape.id));
  }, [tlEditorRef]);



  // const handlePaste = useCallback(() => {
  //   if (isPasting) {
  //     return;
  //   }
  //   isPasting = true;

  //   clearTimeout(pasteTimeout);
  //   pasteTimeout = setTimeout(() => {
  //     if (clipboardContent) {
  //       pasteTldrawContent(tlEditorRef.current, clipboardContent);
  //       isPasting = false;
  //     } else {
  //       navigator.clipboard.readText().then((text) => {
  //         const match = text.match(/<tldraw>(.*)<\/tldraw>/);
  //         if (match && match[1]) {
  //           const content = JSON.parse(decompressFromBase64(match[1]));
  //           pasteTldrawContent(tlEditorRef.current, content);
  //         }
  //         isPasting = false;
  //       }).catch(() => {
  //         isPasting = false;
  //       });
  //     }
  //   }, 100);
  // }, [tlEditorRef]);

  const lastPastePositionRef = useRef(null);
  const lastMousePointRef = useRef({ x: 0, y: 0 });


  let lastPastePosition = null; // Store last paste position

  const pasteTldrawContent = (editor, clipboard, point) => {
    // let p = point ?? (editor.inputs.currentPagePoint ? editor.inputs.currentPagePoint : undefined);

    // const OFFSET = 50; // Adjust offset as needed

    // if (p) {
    //   // If last position exists, further offset to avoid overlap
    //   if (lastPastePosition) {
    //     p = { x: lastPastePosition.x + OFFSET, y: lastPastePosition.y + OFFSET };
    //   } else {
    //     p = { x: p.x + OFFSET, y: p.y + OFFSET };
    //   }
    // }

    // lastPastePosition = p; // Update last paste position

    const OFFSET = 50;

    let currentPoint = editor.inputs.currentPagePoint || { x: 100, y: 100 };

    // Round for stable comparisons
    const round = (val) => Math.round(val);
    const isSamePoint = (p1, p2) =>
      round(p1.x) === round(p2.x) && round(p1.y) === round(p2.y);

    // Initialize refs if not already
    if (!lastMousePointRef.current) lastMousePointRef.current = currentPoint;
    let p;

    if (isSamePoint(currentPoint, lastMousePointRef.current)) {
      // Same mouse position — shift from last paste
      if (lastPastePositionRef.current) {
        p = {
          x: lastPastePositionRef.current.x + OFFSET,
          y: lastPastePositionRef.current.y + OFFSET,
        };
      } else {
        p = currentPoint;
      }
    } else {
      // Mouse moved — start fresh from pointer
      p = currentPoint;
    }


    // ✅ Now update refs AFTER the logic
    lastMousePointRef.current = { ...currentPoint };
    lastPastePositionRef.current = { ...p };

    editor.mark('paste');
    editor.putContentOntoCurrentPage(clipboard, {
      point: p,
      select: true,
    });
  };

  function measureTextWidth(text, font = '16px sans-serif', maxWidth = 400) {
    if (!text) return 0;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;

    const width = context.measureText(text).width;

    // ✅ Only return measured width if it can overflow
    if (width > maxWidth) {
      return width;
    }

    // Otherwise, no need to break → return 0 or just width
    return 200;
  }

  // HANDLE CUSTOM PASTE BEHAVIOUR
  const handlePaste = useCallback((e) => {
    const isWhiteboardElement = e.target?.classList?.contains('tl-container');

    // ✅ Case 1: Outside the whiteboard → let the browser handle paste (inputs, annotations, etc.)
    if (!isWhiteboardElement) {
      return; // no preventDefault, so text boxes work normally
    }

    // ✅ Case 2: Inside the whiteboard → intercept and handle custom paste
    e.preventDefault();
    e.stopPropagation();

    if (isPasting) return;
    if (!isPresenterRef.current && !hasWBAccessRef.current) return;

    let svgFile = null;
    for (let i = 0; i < e.clipboardData.items.length; i++) {
      const item = e.clipboardData.items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          svgFile = file;
        }
        break;
      }
    }

    isPasting = true;
    clearTimeout(pasteTimeout);

    pasteTimeout = setTimeout(async () => {
      const editor = tlEditorRef.current;

      try {
        // ✅ Handle pasted image files (SVG, PNG, JPEG, etc.)
        if (svgFile) {
          const fileType = svgFile.type;
          const fileSize = svgFile.size;
          const reader = new FileReader();

          reader.onload = () => {
            const result = reader.result;
            let src;

            if (fileType === 'image/svg+xml') {
              const base64 = btoa(unescape(encodeURIComponent(result)));
              src = `data:image/svg+xml;base64,${base64}`;
            } else {
              src = result; // already base64 for PNG/JPEG
            }

            const img = new Image();
            img.onload = async () => {
              const assetId = `asset:${Date.now()}`;
              const currentPoint = editor.inputs.currentPagePoint || { x: 100, y: 100 };

              const { width, height } = img;
              const maxSize = 300;
              const scaleUpFactor = 2;// ⬅️ make image 20% larger after resize

              let w = width;
              let h = height;

              if (w > h && w > maxSize) {
                const scale = maxSize / w;
                w = Math.round(maxSize * scaleUpFactor);
                h = Math.round(h * scale * scaleUpFactor);
              } else if (h >= w && h > maxSize) {
                const scale = maxSize / h;
                h = Math.round(maxSize * scaleUpFactor);
                w = Math.round(w * scale * scaleUpFactor);
              }
              const { compressedSrc, mimeType } = await compressImage(src, fileType, fileSize, src);

              await editor.createAssets([
                {
                  id: assetId,
                  typeName: 'asset',
                  type: 'image',
                  props: {
                    src: compressedSrc,
                    w,
                    h,
                    name: 'Pasted Image',
                    mimeType: fileType,
                    isAnimated: false,
                  },
                  meta: {},
                },
              ]);


              if (editor.store.has(assetId)) {
                editor.createShape({
                  id: `shape:${Date.now()}`,
                  type: "image",
                  x: currentPoint.x ?? 100,
                  y: currentPoint.y ?? 100,
                  props: { assetId, w, h },
                });
                editor.setCurrentTool("select");
              }
              //    setTimeout(() => {
              // }, 500);
            };

            img.onerror = () => {
              console.error('Failed to load image');
              isPasting = false;
            };

            img.src = src;
          };

          if (fileType === 'image/svg+xml') {
            reader.readAsText(svgFile);
          } else {
            reader.readAsDataURL(svgFile);
          }

          return;
        }

        // ✅ Fallback: plain text or TLDraw JSON
        const text = await navigator.clipboard.readText();
        const match = text.match(/<tldraw>(.*?)<\/tldraw>/);

        if (match && match[1]) {
          const parsed = JSON.parse(decompressFromBase64(match[1]));
          clipboardContent = parsed.data;
          pasteTldrawContent(editor, parsed.data);
        } else if (text.trim()) {
          // Create text shape
          const OFFSET = 50;
          let currentPoint = editor.inputs.currentPagePoint || { x: 100, y: 100 };

          const round = (val) => Math.round(val);
          const isSamePoint = (p1, p2) =>
            round(p1.x) === round(p2.x) && round(p1.y) === round(p2.y);

          if (!lastMousePointRef.current) lastMousePointRef.current = currentPoint;
          let p;

          if (isSamePoint(currentPoint, lastMousePointRef.current)) {
            if (lastPastePositionRef.current) {
              p = {
                x: lastPastePositionRef.current.x + OFFSET,
                y: lastPastePositionRef.current.y + OFFSET,
              };
            } else {
              p = currentPoint;
            }
          } else {
            p = currentPoint;
          }

          lastMousePointRef.current = { ...currentPoint };
          lastPastePositionRef.current = { ...p };

          const fontSize = 16;
          const fontStyle = `${fontSize}px sans-serif`;
          const textWidth = measureTextWidth(text, fontStyle);

          const id = `shape:${Date.now()}`;
          const shape = {
            id,
            type: 'text',
            x: p.x,
            y: p.y,
            props: {
              text,
              font: 'sans',
              size: 'm',
              align: 'start',
              color: 'black',
              w: Math.min(textWidth, 500),
              autoSize: false,
            },
          };

          editor.createShape(shape);
          editor.setCurrentTool('select');
        } else if (clipboardContent) {
          pasteTldrawContent(editor, clipboardContent);
        }

      } catch (err) {
        console.error('Clipboard paste failed:', err);
        if (clipboardContent) {
          pasteTldrawContent(editor, clipboardContent);
        }
      } finally {
        isPasting = false;
        editor.setCurrentTool('select')
      }
    }, 100);
  }, [tlEditorRef, isPresenterRef, hasWBAccessRef]);


  const handleKeyDown = useCallback((event) => {
    if (event.repeat) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // ignore if the edit link dialog is open
    if (document.querySelector('h2.tlui-dialog__header__title')?.textContent === 'Edit link') {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'escape' || event.keyCode === 27) {
      tlEditorRef.current?.deselect(...tlEditorRef.current?.getSelectedShapes());
      return;
    }

    const editingShape = tlEditorRef.current?.getEditingShape();
    if (editingShape && (isPresenterRef.current || hasWBAccessRef.current)) {
      return;
    }

    if (['delete', 'backspace'].includes(key.toLowerCase())) {
      handleCut(false);
      return;
    }

    if (key === ' ' && tlEditorRef.current?.getCurrentToolId() !== 'hand' && isPresenterRef.current) {
      previousTool.current = tlEditorRef.current?.getCurrentToolId();
      tlEditorRef.current?.setCurrentTool('hand');
      return;
    }

    // Mapping of simple key shortcuts to tldraw functions

    // Mapping of simple key shortcuts to tldraw functions
    const simpleKeyMap = {
      // Combos
      1: () => tlEditorRef.current?.setCurrentTool('select'),
      2: () => tlEditorRef.current?.setCurrentTool('draw'),
      3: () => tlEditorRef.current?.setCurrentTool('eraser'),
      4: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'rectangle');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      5: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'ellipse');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      6: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'triangle');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      7: () => tlEditorRef.current?.setCurrentTool('line'),
      8: () => tlEditorRef.current?.setCurrentTool('arrow'),
      9: () => tlEditorRef.current?.setCurrentTool('text'),
      0: () => tlEditorRef.current?.setCurrentTool('note'),

      // Alternatives
      v: () => tlEditorRef.current?.setCurrentTool('select'),
      d: () => tlEditorRef.current?.setCurrentTool('draw'),
      p: () => tlEditorRef.current?.setCurrentTool('draw'),
      g: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'triangle');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      '[': () => {
        tlEditorRef.current?.sendBackward(tlEditorRef.current?.getSelectedShapes());
      },
      ']': () => {
        tlEditorRef.current?.bringForward(tlEditorRef.current?.getSelectedShapes());
      },
      e: () => tlEditorRef.current?.setCurrentTool('eraser'),
      h: () => {
        if (isPresenterRef.current) {
          tlEditorRef.current?.setCurrentTool('hand');
        }
      },
      r: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'rectangle');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      o: () => {
        tlEditorRef.current?.setStyleForNextShapes(GeoShapeGeoStyle, 'ellipse');
        tlEditorRef.current?.setCurrentTool('geo');
      },
      a: () => tlEditorRef.current?.setCurrentTool('arrow'),
      l: () => tlEditorRef.current?.setCurrentTool('line'),
      t: () => tlEditorRef.current?.setCurrentTool('text'),
      f: () => tlEditorRef.current?.setCurrentTool('frame'),
      n: () => tlEditorRef.current?.setCurrentTool('note'),
      s: () => tlEditorRef.current?.setCurrentTool('note'),
    };


    if (event.ctrlKey || event.metaKey) {
      if (key === 'z') {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          // Redo (Ctrl + Shift + z)
          tlEditorRef.current?.redo();
        } else {
          // Undo (Ctrl + z)
          tlEditorRef.current?.undo();
        }
        return;
      }
      if (key === 'u') {
        event.preventDefault();
        event.stopPropagation();
        if (tlEditorRef.current) {
          handleAssetSelection(tlEditorRef.current)
          // tlEditorRef.current.setCurrentTool("media");
        }
        // tlEditorRef.current.setCurrentTool("asset");
      }

      const ctrlKeyMap = {
        a: () => {
          tlEditorRef.current?.selectAll();
          tlEditorRef.current?.setCurrentTool('select');
        },
        d: () => {
          tlEditorRef.current
            ?.duplicateShapes(tlEditorRef.current?.getSelectedShapes(), { x: 35, y: 35 });
          tlEditorRef.current?.selectNone();
        },
        x: () => {
          handleCut(true);
        },
        c: () => {
          handleCopy();
        },
        // v: () => {
        //   if (!isPasting) {
        //     handlePaste(event);
        //   }
        // },
      };

      // Allow paste events (Cmd+V) to pass through to the paste event handler
      if (key === 'v') {
        // Don't prevent default or stop propagation for paste
        return;
      }

      if (ctrlKeyMap[key]) {
        event.preventDefault();
        event.stopPropagation();
        ctrlKeyMap[key]();
        return;
      }
    }

    if (!event.altKey && !event.ctrlKey && !event.shiftKey && simpleKeyMap[key]) {
      event.preventDefault();
      event.stopPropagation();
      simpleKeyMap[key]();
      return;
    }

    const moveDistance = 10;
    const selectedShapes = tlEditorRef.current?.getSelectedShapes().map((shape) => shape.id);

    const arrowKeyMap = {
      ArrowUp: { x: 0, y: -moveDistance },
      ArrowDown: { x: 0, y: moveDistance },
      ArrowLeft: { x: -moveDistance, y: 0 },
      ArrowRight: { x: moveDistance, y: 0 },
    };

    if (arrowKeyMap[event.key]) {
      event.preventDefault();
      event.stopPropagation();
      // tlEditorRef.current?.nudgeShapes(selectedShapes, arrowKeyMap[event.key], { squashing: true });
    }
  }, [
    tlEditorRef, isPresenterRef, hasWBAccessRef, previousTool, handleCut, handleCopy
  ]);

  React.useEffect(() => {
    if (whiteboardRef.current) {
      whiteboardRef.current.addEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    }
    return () => {
      whiteboardRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [whiteboardRef.current]);

  // React.useEffect(() => {
  //   window.addEventListener('paste', handlePaste, true);
  //   return () => window.removeEventListener('paste', handlePaste, true);
  // }, [handlePaste]);
  React.useEffect(() => {
    const el = whiteboardRef.current;
    if (!el) return;
    el.addEventListener('paste', handlePaste, true);
    return () => el.removeEventListener('paste', handlePaste, true);
  }, [handlePaste]);


  const language = React.useMemo(() => mapLanguage(locale?.toLowerCase() || 'en'), [locale]);

  const [cursorPosition, updateCursorPosition] = useCursor(
    publishCursorUpdate,
    whiteboardIdRef.current,
  );

  const setCamera = (zoom, x = 0, y = 0) => {
    if (tlEditorRef.current) {
      // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
      tlEditorRef.current.store.mergeRemoteChanges(() => {
        tlEditorRef.current.setCamera({ x, y, z: zoom }, { duration: 175 });
      });
    }
  };

  const calculateZoomValue = (localWidth, localHeight) => {
    // Prioritize fitting to presentation height to ensure content fits within height
    // This prevents vertical scrolling while allowing some width flexibility
    const calcedZoom = Math.min(
      presentationAreaHeight / localHeight,  // Prioritize height fitting
      presentationAreaWidth / localWidth,    // Secondary width constraint
    );
    return calcedZoom === 0 || calcedZoom === Infinity || Number.isNaN(calcedZoom)
      ? HUNDRED_PERCENT
      : calcedZoom;
  };

  const getContainerDimensions = () => {
    const container = document.querySelector('[data-test="presentationContainer"]');
    const innerWrapper = document.getElementById('presentationInnerWrapper');
    const containerWidth = container ? container.offsetWidth : 0;
    const innerWrapperWidth = innerWrapper ? innerWrapper.offsetWidth : 0;
    const widthGap = Math.max(containerWidth - innerWrapperWidth, 0);
    return { containerWidth, innerWrapperWidth, widthGap };
  }

  const coreCameraLogic = ({
    baseZoom,
    xOffset,
    yOffset,
    description,
    tlEditorRef,
    curPageIdRef,
  }) => {
    const throwIfInvalid = (value, desc) => {
      if (!Number.isFinite(value)) {
        throw new Error(`Invalid ${desc}: ${value}`);
      }
    };

    const validateCameraObject = (cam) => {
      try {
        throwIfInvalid(cam.x, 'camera.x');
        throwIfInvalid(cam.y, 'camera.y');
        throwIfInvalid(cam.z, 'camera.z');
      } catch (error) {
        logger.error(
          {
            logCode: 'ValidateCameraObject',
            extraInfo: {
              camera: cam,
            },
          },
          `Invalid camera object: ${error}`
        );
        return false;
      }
      return true;
    };

    throwIfInvalid(baseZoom, `baseZoom ${description}`);
    throwIfInvalid(xOffset, `camera.x ${description}`);
    throwIfInvalid(yOffset, `camera.y ${description}`);

    const camera = tlEditorRef.current.getCamera();
    const formattedPageId = Number(curPageIdRef.current);

    if (Number.isNaN(formattedPageId)) {
      throw new Error(`Invalid formattedPageId ${description}: ${formattedPageId}`);
    }

    const updatedCurrentCam = {
      ...camera,
      x: xOffset,
      y: yOffset,
      z: baseZoom,
    };

    tlEditorRef.current.store.put([updatedCurrentCam]);
  }

  const adjustCameraOnMount = (includeViewerLogic = true) => {

    try {
      if (presenterChanged) {
        localStorage.removeItem('initialViewBoxWidth');
        localStorage.removeItem('initialViewBoxHeight');
      }

      const storedWidth = localStorage.getItem('initialViewBoxWidth');
      const storedHeight = localStorage.getItem('initialViewBoxHeight');

      const throwIfInvalid = (val, desc) => {
        if (!Number.isFinite(val)) {
          throw new Error(`Invalid ${desc}: ${val}`);
        }
      };

      if (storedWidth && storedHeight) {
        const parsedWidth = parseFloat(storedWidth);
        const parsedHeight = parseFloat(storedHeight);
        throwIfInvalid(parsedWidth, 'stored initialViewBoxWidth');
        throwIfInvalid(parsedHeight, 'stored initialViewBoxHeight');

        initialViewBoxWidthRef.current = parsedWidth;
        initialViewBoxHeightRef.current = parsedHeight;
      } else {
        const currentPage = currentPresentationPageRef.current;
        const { scaledWidth, scaledHeight, scaledViewBoxWidth, scaledViewBoxHeight } = currentPage;

        if (scaledViewBoxWidth === 0 || scaledViewBoxHeight === 0) {
          throw new Error(
            `scaledViewBoxWidth or scaledViewBoxHeight is zero: ${scaledViewBoxWidth}, ${scaledViewBoxHeight}`
          );
        }

        const currentZoomLevel = scaledWidth / scaledViewBoxWidth;
        throwIfInvalid(currentZoomLevel, 'currentZoomLevel');

        const calculatedWidth =
          currentZoomLevel !== 1 ? scaledWidth / currentZoomLevel : scaledWidth;
        const calculatedHeight =
          currentZoomLevel !== 1 ? scaledHeight / currentZoomLevel : scaledHeight;

        throwIfInvalid(calculatedWidth, 'calculatedWidth');
        throwIfInvalid(calculatedHeight, 'calculatedHeight');

        initialViewBoxWidthRef.current = calculatedWidth;
        initialViewBoxHeightRef.current = calculatedHeight;

        try {
          localStorage.setItem('initialViewBoxWidth', calculatedWidth.toString());
          localStorage.setItem('initialViewBoxHeight', calculatedHeight.toString());
        } catch (error) {
          logger.warn(
            { logCode: 'InitialViewBoxStorage' },
            `Failed to store viewbox dimensions: ${error}`
          );
        }
      }

      const {
        scaledWidth,
        scaledHeight,
        scaledViewBoxWidth,
        scaledViewBoxHeight,
        xOffset,
        yOffset,
      } = currentPresentationPageRef.current;

      if (
        presentationAreaHeight > 0 &&
        presentationAreaWidth > 0 &&
        scaledWidth > 0 &&
        scaledHeight > 0 &&
        tlEditorRef.current
      ) {
        const adjustedPresentationAreaHeight = isPresenterRef.current
          ? presentationAreaHeight - 40
          : presentationAreaHeight;

        let baseZoom = calculateZoomValue(scaledWidth, scaledHeight);
        throwIfInvalid(baseZoom, 'baseZoom');

        if (isPresenterRef.current) {
          const { containerWidth, innerWrapperWidth, widthGap } = getContainerDimensions();
          if (widthGap > 0) {
            const zoomWithGap = calculateZoomWithGapValue(scaledWidth, scaledHeight, widthGap);
            throwIfInvalid(zoomWithGap, 'zoomWithGap');
            baseZoom = zoomWithGap;
          }

          // IMPROVED: Always center camera for presenters initially with proper timing
          // Wait for the background shape to be available before centering
          const centerCameraOnBackgroundShape = () => {
            const slideShape = tlEditorRef.current.getShape(`shape:BG-${curPageIdRef.current}`);
            if (slideShape) {
              const viewportScreenBounds = tlEditorRef.current.getViewportScreenBounds();
              const viewportWidth = viewportScreenBounds.width;
              const viewportHeight = viewportScreenBounds.height;

              // Calculate center position accounting for the background shape's actual position
              const centeredCameraX = -slideShape.x + (viewportWidth - slideShape.props.w * baseZoom) / (2 * baseZoom);
              const centeredCameraY = -slideShape.y + (viewportHeight - slideShape.props.h * baseZoom) / (2 * baseZoom);

              // Check if this is the first time centering this slide
              const slideKey = `${presentationIdRef.current}-${curPageIdRef.current}`;
              const isFirstTime = !isFirstTimeCentering[slideKey];

              if (isFirstTime) {
                // First time centering - set camera to center and sync with viewers
                // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
                tlEditorRef.current.store.mergeRemoteChanges(() => {
                  tlEditorRef.current.setCamera({
                    x: centeredCameraX,
                    y: centeredCameraY,
                    z: baseZoom
                  }, { duration: 0 });
                });

                // Mark as first time done
                setIsFirstTimeCentering(prev => ({ ...prev, [slideKey]: true }));

                // Synchronize camera centering with viewers through zoomSlide
                if (isPresenterRef.current && currentPresentationPageRef.current && currentPresentationPageRef.current.scaledWidth && currentPresentationPageRef.current.scaledHeight) {
                  // Use setTimeout to ensure store operations are complete before calling zoomSlide
                  setTimeout(() => {
                    try {
                      const viewedRegionW = SlideCalcUtil.calcViewedRegionWidth(
                        tlEditorRef.current.getViewportPageBounds()?.w || viewportWidth / baseZoom,
                        currentPresentationPageRef.current.scaledWidth,
                      );
                      const viewedRegionH = SlideCalcUtil.calcViewedRegionHeight(
                        tlEditorRef.current.getViewportPageBounds()?.h || viewportHeight / baseZoom,
                        currentPresentationPageRef.current.scaledHeight,
                      );

                      // console.log('First time centering - synchronizing with viewers:', {
                      //   slideId: curPageIdRef.current,
                      //   viewedRegionW,
                      //   viewedRegionH,
                      //   camera: { x: centeredCameraX, y: centeredCameraY, z: baseZoom },
                      //   isPresenter: isPresenterRef.current,
                      //   hasPresentationPage: !!currentPresentationPageRef.current
                      // });
                      zoomSlide(
                        viewedRegionW, viewedRegionH, centeredCameraX, centeredCameraY,
                        currentPresentationPageRef.current,
                      );
                    } catch (error) {
                      console.warn('Failed to synchronize first-time camera centering:', error);
                    }
                  }, 50); // Small delay to ensure store is ready
                }
              } else {
                // Not first time - keep presenter's current camera position (don't force center)
                // console.log('Subsequent initial centering - keeping presenter current position (no centering, no viewer sync):', {
                //   slideId: curPageIdRef.current,
                //   currentCamera: tlEditorRef.current.getCamera(),
                //   isFirstTime: false
                // });
                // Don't call setCamera - let presenter keep their current position
              }
            } else {
              // If background shape not ready yet, retry after a short delay
              setTimeout(centerCameraOnBackgroundShape, 100);
            }
          };

          // Start centering process with a small delay to ensure background shape is ready
          setTimeout(centerCameraOnBackgroundShape, 50);

          coreCameraLogic({
            baseZoom,
            xOffset,
            yOffset,
            description: '(presenter)',
            tlEditorRef,
            curPageIdRef,
          });
        } else if (includeViewerLogic) {

          // Viewer logic - KEEP AS IS (working fine)
          baseZoom = calculateZoomValue(scaledViewBoxWidth, scaledViewBoxHeight);
          coreCameraLogic({
            baseZoom,
            xOffset,
            yOffset,
            description: '(viewer)',
            tlEditorRef,
            curPageIdRef,
          });
        }

        isMountedRef.current = true;
      }
    } catch (error) {
      logger.error({ logCode: 'AdjustCameraOnMount' }, `Failed to store viewbox: ${error}`);
      throw error;
    }
  }

  const pollInnerWrapperDimensionsUntilStable = (
    onReady,
    options = {
      maxTries: 120,
      stabilityFrames: 50,
    },
    frameIdRef = null,
    currentTry = 0,
    stableCount = 0,
    lastDimensions = { width: 0, height: 0 }
  ) => {
    const container = document.querySelector('[data-test="presentationContainer"]');
    const innerWrapper = document.getElementById('presentationInnerWrapper');

    const containerWidth = container ? container.offsetWidth : 0;
    const containerHeight = container ? container.offsetHeight : 0;
    const innerWrapperWidth = innerWrapper ? innerWrapper.offsetWidth : 0;
    const innerWrapperHeight = innerWrapper ? innerWrapper.offsetHeight : 0;

    if (innerWrapperWidth <= 0 || innerWrapperHeight <= 0) {
      stableCount = 0;
    } else {
      if (
        innerWrapperWidth === lastDimensions.width &&
        innerWrapperHeight === lastDimensions.height
      ) {
        stableCount++;
      } else {
        stableCount = 0;
        lastDimensions = { width: innerWrapperWidth, height: innerWrapperHeight };
      }
    }

    if (stableCount >= options.stabilityFrames) {
      onReady({ containerWidth, containerHeight, innerWrapperWidth, innerWrapperHeight });
      return;
    }

    if (currentTry < options.maxTries) {
      const frameId = requestAnimationFrame(() => {
        pollInnerWrapperDimensionsUntilStable(
          onReady,
          options,
          frameIdRef,
          currentTry + 1,
          stableCount,
          lastDimensions
        );
      });
      if (frameIdRef) {
        frameIdRef.current = frameId;
      }
    } else {
      logger.warn(
        { logCode: 'pollInnerWrapperDimensionsUntilStable' },
        `Failed to store viewbox dimensions`
      );
      onReady({ containerWidth, containerHeight, innerWrapperWidth, innerWrapperHeight });
    }
  };

  const pollUntilMounted = (onReady, onFail, ref = null, options = { maxTries: 240 }, currentTry = 0) => {
    if (isMountedRef.current) {
      onReady();
    } else if (currentTry <= options.maxTries) {
      const frameId = requestAnimationFrame(() => {
        pollUntilMounted(onReady, onFail, ref, options, currentTry + 1);
      });
      if (ref) {
        ref.current = frameId;
      }
    } else {
      onFail();
    }
  };

  /**
   * Updates zoom constraints to prevent users from zooming out below 100% (fit-to-height level)
   * This ensures users always maintain proper context and don't lose sight of the content
   * 
   * Zoom limits:
   * - Minimum: 100% (baseZoom - fit-to-height level)
   * - Maximum: 400% (4x zoom for detailed work)
   * 
   * @param {Object} editor - The tldraw editor instance
   */
  const updateZoomConstraints = (editor) => {
    if (!editor || !currentPresentationPageRef.current) return;

    const baseZoom = calculateZoomValue(
      currentPresentationPageRef.current.scaledWidth,
      currentPresentationPageRef.current.scaledHeight
    );

    // Enforce zoom constraints by updating camera if needed
    // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
    const currentCamera = editor.getCamera();
    if (currentCamera.z < baseZoom) {
      editor.store.mergeRemoteChanges(() => {
        editor.setCamera({ ...currentCamera, z: baseZoom });
      });
    }

    // Additional safeguard: Set a global zoom constraint flag
    editor.updateInstanceState({
      canMoveCamera: true,
      isReadonly: false,
      // Add custom metadata to track zoom constraints
      meta: {
        ...editor.getInstanceState().meta,
        zoomConstraints: {
          minZoom: baseZoom,
          maxZoom: baseZoom * 4,
          enforced: true
        }
      }
    });
  };

  /**
   * Robust zoom constraint enforcement that runs periodically and catches violations
   * This provides multiple layers of protection against zooming out below 100%
   */
  const enforceZoomConstraints = (editor) => {
    if (!editor || !currentPresentationPageRef.current) return;

    const baseZoom = calculateZoomValue(
      currentPresentationPageRef.current.scaledWidth,
      currentPresentationPageRef.current.scaledHeight
    );

    const currentCamera = editor.getCamera();
    let needsUpdate = false;
    let newCamera = { ...currentCamera };

    // Enforce minimum zoom (100%)
    if (currentCamera.z < baseZoom) {
      newCamera.z = baseZoom;
      needsUpdate = true;
    }

    // Enforce maximum zoom (400%)
    if (currentCamera.z > baseZoom * 4) {
      newCamera.z = baseZoom * 4;
      needsUpdate = true;
    }

    // Apply constraints if needed
    // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
    if (needsUpdate) {
      editor.store.mergeRemoteChanges(() => {
        editor.setCamera(newCamera);
      });
      // console.log(`Zoom constrained: ${currentCamera.z.toFixed(2)} → ${newCamera.z.toFixed(2)}`);
    }
  };

  const handleTldrawMount = (editor) => {

    tlEditorRef.current = editor;
    setTldrawAPI(editor);
    setEditor(editor);

    // Expose editor globally for whiteboard state capture from other components
    // Used by: app/container.jsx, end-meeting-confirmation, leave-meeting-button
    window.tlEditorRef = tlEditorRef;

    // REMOVED ALL ZOOM CONSTRAINTS - Allow complete freedom for presenters
    // Viewers will sync to presenter's view automatically
    const baseZoom = calculateZoomValue(
      currentPresentationPageRef.current?.scaledWidth || 1000,
      currentPresentationPageRef.current?.scaledHeight || 1000
    );

    // IMPROVED: Set initial camera with proper centering
    // Don't set camera here - let adjustCameraOnMount handle it properly
    // This ensures the background shape is available before positioning
    editor.updateInstanceState({
      canMoveCamera: true,
      isReadonly: false,
      meta: {
        ...editor.getInstanceState().meta,
        zoomConstraints: {
          minZoom: 0.01,
          enforced: true
        }
      }

    });

    DefaultHorizontalAlignStyle.defaultValue = isRTL ? 'end' : 'start';
    DefaultVerticalAlignStyle.defaultValue = 'start';

    editor?.user?.updateUserPreferences({ locale: language });

    const colorStyles = [
      'black',
      'blue',
      'green',
      'grey',
      'light-blue',
      'light-green',
      'light-red',
      'light-violet',
      'orange',
      'red',
      'violet',
      'yellow',
    ];
    const dashStyles = ['dashed', 'dotted', 'draw', 'solid'];
    const fillStyles = ['none', 'pattern', 'semi', 'solid'];
    const fontStyles = ['draw', 'mono', 'sans', 'serif'];
    const sizeStyles = ['l', 'm', 's', 'xl'];

    if (colorStyles.includes(colorStyle)) {
      editor.setStyleForNextShapes(DefaultColorStyle, colorStyle);
    }
    if (dashStyles.includes(dashStyle)) {
      editor.setStyleForNextShapes(DefaultDashStyle, dashStyle);
    }
    if (fillStyles.includes(fillStyle)) {
      editor.setStyleForNextShapes(DefaultFillStyle, fillStyle);
    }

    if (fontStyles.includes(fontStyle)) {
      editor.setStyleForNextShapes(DefaultFontStyle, fontStyle);
    }
    if (sizeStyles.includes(sizeStyle)) {
      editor.setStyleForNextShapes(DefaultSizeStyle, sizeStyle);
    }

    editor.store.listen(
      (entry) => {
        const { changes } = entry;
        const { added, updated, removed } = changes;
        // if (!added) return

        // Loop through all records that were added
        // for (const record of Object.values(added)) {
        //   if (record.typeName === "asset" && record.type === "image") {

        //     const assetId = record.id
        //     const { w, h } = record.props
        //     const { x, y } = record.meta ?? {}

        //     editor.createShape({
        //       id: `shape:${Date.now()}`,
        //       type: "image",
        //       x: x ?? 100,
        //       y: y ?? 100,
        //       props: { assetId, w, h },
        //     })

        //     editor.setCurrentTool("select")
        //   }
        // }
        // REMOVED ALL ZOOM CONSTRAINTS - Allow complete freedom for presenters
        // Viewers will sync to presenter's view automatically
        // const camKey = `camera:page:${curPageIdRef.current}`;
        // if (updated[camKey]) {
        //   const [, nextCam] = updated[camKey];
        //   if (nextCam && nextCam.z) {
        //     const baseZoom = calculateZoomValue(
        //       currentPresentationPageRef.current?.scaledWidth || 1000,
        //       currentPresentationPageRef.current?.scaledHeight || 1000
        //     );

        //     // Prevent zooming out below 100% - with additional safeguards
        //     if (nextCam.z < baseZoom) {
        //       console.log(`Blocking zoom out: ${nextCam.z.toFixed(2)} → ${baseZoom.toFixed(2)} (100%)`);
        //       editor.setCamera({ ...nextCam, z: baseZoom });
        //       return;
        //     }

        //     // Prevent zooming in above 400% - with additional safeguards
        //     if (nextCam.z > baseZoom * 4) {
        //       console.log(`Blocking zoom in: ${nextCam.z.toFixed(2)} → ${(baseZoom * 4).toFixed(2)} (400%)`);
        //       editor.setCamera({ ...nextCam, z: baseZoom * 4 });
        //       return;
        //     }

        //     // Additional check: ensure zoom stays within reasonable bounds
        //     if (nextCam.z < baseZoom * 0.5 || nextCam.z > baseZoom * 5) {
        //       console.log(`Resetting extreme zoom: ${nextCam.z.toFixed(2)} → ${baseZoom.toFixed(2)}`);
        //       editor.setCamera({ ...nextCam, z: baseZoom });
        //       return;
        //     }
        //   }
        // }

        // ADDED: Only prevent zooming above 400% for presenters
        const camKey = `camera:page:${curPageIdRef.current}`;
        if (updated[camKey] && isPresenterRef.current) {
          const [, nextCam] = updated[camKey];
          if (nextCam && nextCam.z) {
            const baseZoom = calculateZoomValue(
              currentPresentationPageRef.current?.scaledWidth || 1000,
              currentPresentationPageRef.current?.scaledHeight || 1000
            );

            // Prevent zooming out below 100% (fit-to-height level)
            // Use mergeRemoteChanges to prevent infinite loop
            if (nextCam.z < baseZoom) {
              editor.store.mergeRemoteChanges(() => {
                editor.setCamera({ ...nextCam, z: baseZoom });
              });
              return;
            }

            // Prevent zooming in above 400% - allow free zooming out
            // Use mergeRemoteChanges to prevent infinite loop
            if (nextCam.z > baseZoom * 4) {
              editor.store.mergeRemoteChanges(() => {
                editor.setCamera({ ...nextCam, z: baseZoom * 4 });
              });
              return;
            }
          }
        }

        const addedCount = Object.keys(added).length;
        const localShapes = editor.getCurrentPageShapes();
        const filteredShapes = localShapes?.filter((item) => item?.index !== 'a0') || [];

        const shapeNumberExceeded = filteredShapes
          .length + addedCount - 1 > maxNumberOfAnnotations;
        const invalidShapeType = Object.keys(added).find((id) => !isValidShapeType(added[id]));

        const ANNOTATION_WARNING_THRESHOLD = 4000;
        const shapeCountBefore = filteredShapes.length - addedCount;
        const shapeCountAfter = filteredShapes.length;
        if (addedCount > 0 && shapeCountBefore < ANNOTATION_WARNING_THRESHOLD && shapeCountAfter >= ANNOTATION_WARNING_THRESHOLD) {
          notifyShapeNumberWarning(intl, ANNOTATION_WARNING_THRESHOLD);
        }

        if (addedCount > 0 && (shapeNumberExceeded || invalidShapeType)) {
          // notify and undo last command without persisting
          // to not generate the onUndo/onRedo callback
          if (shapeNumberExceeded) {
            notifyShapeNumberExceeded(intl, maxNumberOfAnnotations);
          } else {
            notifyNotAllowedChange(intl);
          }
          // use remote to not trigger unwanted updates
          editor.store.mergeRemoteChanges(() => {
            editor.history.undo({ persist: false });
            const tool = editor.getCurrentToolId();
            editor.setCurrentTool('noop');
            editor.setCurrentTool(tool);
          });
        } else {
          // Add new shapes to the batch
          Object.values(added).forEach((record) => {
            const updatedRecord = {
              ...record,
              meta: {
                ...record.meta,
                createdBy: currentUser?.userId,
                presentationId: presentationIdRef.current,
              },
            };

            shapeBatchRef.current[updatedRecord.id] = updatedRecord;
          });
        }

        // Update existing shapes and add them to the batch
        Object.values(updated).forEach(([, record]) => {
          const formattedLookup = createLookup(editor.getCurrentPageShapes());
          const createdBy = formattedLookup[record?.id]?.meta?.createdBy || currentUser?.userId;
          const updatedRecord = {
            ...record,
            meta: {
              createdBy,
              updatedBy: currentUser?.userId,
              presentationId: presentationIdRef.current,
            },
          };

          const diff = getDifferences(prevShapesRef.current[record?.id], updatedRecord);

          if (diff) {
            diff.id = record.id;

            shapeBatchRef.current[updatedRecord.id] = diff;
          } else {
            shapeBatchRef.current[updatedRecord.id] = updatedRecord;
          }
        });

        // Handle removed shapes immediately (not batched)
        const idsToRemove = Object.keys(removed);
        if (idsToRemove.length > 0) {
          // Remove from batch
          // idsToRemove.forEach((id) => {
          //   delete shapeBatchRef.current[id];
          // });
          removeShapes(idsToRemove);
        }
      },
      { source: 'user', scope: 'document' },
    );

    editor.store.listen(
      (entry) => {
        const { changes } = entry;
        const { updated, added } = changes;

        const { 'pointer:pointer': pointers } = updated;
        const path = editor.getPath();

        if ((isPresenterRef.current || hasWBAccessRef.current) && pointers) {
          const [, nextPointer] = pointers;
          updateCursorPosition(nextPointer?.x, nextPointer?.y);
        }
        const camKey = `camera:page:${curPageIdRef.current}`;
        const { [camKey]: cameras } = updated;

        if (cameras) {

          const [prevCam, nextCam] = cameras;
          const panned = prevCam.x !== nextCam.x || prevCam.y !== nextCam.y;
          //  || (isModerator && hasWBAccessRef.current)
          const zoomed = prevCam.z !== nextCam.z;
          if ((panned || (zoomed && fitToWidthRef.current)) && (isPresenterRef.current)) {
            const viewedRegionW = SlideCalcUtil.calcViewedRegionWidth(
              editor?.getViewportPageBounds()?.w,
              currentPresentationPageRef.current?.scaledWidth,
            );
            const viewedRegionH = SlideCalcUtil.calcViewedRegionHeight(
              editor?.getViewportPageBounds()?.h,
              currentPresentationPageRef.current?.scaledHeight,
            );

            if (isMountedRef.current) {
              zoomSlide(
                viewedRegionW, viewedRegionH, nextCam.x, nextCam.y,
                currentPresentationPageRef.current,
              );
            }
          }
        }
        // Check for idle states and persist the batch if there are shapes
       
        if (path === 'select.idle' || path === 'draw.idle' ||
          path === 'select.editing_shape' || path === 'highlight.idle' ||
          path === 'geo.idle' || path === 'frame.idle' || path === 'line.idle' || path === 'arrow.idle') {

          if (Object.keys(shapeBatchRef.current).length > 0) {
            const shapesToPersist = Object.values(shapeBatchRef.current);
            // Get all current shapes from the editor

            shapesToPersist.forEach((shape) => {
              persistShapeWrapper(
                shape,
                whiteboardIdRef.current,
                isModeratorRef.current,
              );
            });
          }
          shapeBatchRef.current = {};
        }
        if (path === 'note.pointing') {
          editor.setCurrentTool('select')
        }
      },
      { source: 'user' },
    );

    if (editor && curPageIdRef.current) {
      const page = [];
      const formattedPageId = parseInt(curPageIdRef.current, 10);
      const currentPageId = `page:${formattedPageId}`;
      const currPageExists = tlEditorRef.current?.getPage(currentPageId);

      if (!currPageExists) {
        const currentPage = createPage(currentPageId);
        page.push(...currentPage);
      }

      const hasShapes = shapes && Object.keys(shapes).length > 0;

      const remoteShapesArray = hasShapes
        ? Object.values(shapes).map((shape) => sanitizeShape(shape))
        : [];
      editor.store.mergeRemoteChanges(() => {
        editor.batch(() => {
          editor.store.put(page);
          editor.store.put(assets);
          editor.setCurrentPage(`page:${curPageIdRef.current}`);
          editor.store.put(bgShape);
          editor?.store.put(rectShape)
          if (hasShapes) {
            editor.store.put(remoteShapesArray);
          }
          editor.history.clear();
        });
      });

      // eslint-disable-next-line no-param-reassign
      editor.store.onBeforeChange = (prev, next) => {
        if (isPhone) {
          const path = editor.getPath();
          const activePaths = [
            'draw.drawing',
            'eraser.erasing',
            'select.dragging_handle',
            'select.resizing',
            'select.translating',
            'select.rotating',
            'select.editing_shape',
            'hand.pointing',
            'hand.dragging',
            'geo.pointing',
            'line.pointing',
            'highlight.drawing',
          ];
          const idlePaths = [
            'draw.idle',
            'eraser.idle',
            'select.idle',
            'hand.idle',
            'highlight.idle',
          ];

          let visibilityState = null;
          if (activePaths.includes(path)) {
            visibilityState = 'visible';
          } else if (idlePaths.includes(path)) {
            visibilityState = 'hidden';
          }

          if (visibilityState && visibilityState !== lastVisibilityStateRef.current) {
            if (visibilityState === 'visible') {
              toggleToolsAnimations(
                'fade-in',
                'fade-out',
                '0s',
                hasWBAccessRef.current || isPresenterRef.current,
              );
            } else if (visibilityState === 'hidden') {
              toggleToolsAnimations(
                'fade-out',
                'fade-in',
                '0s',
                hasWBAccessRef.current || isPresenterRef.current,
              );
            }
            lastVisibilityStateRef.current = visibilityState;
          }
        }

        const newNext = next;
        if (next?.typeName === 'instance_page_state') {
          if (isPresenterRef.current || isModeratorRef.current) return next;
          const formattedLookup = createLookup(editor.getCurrentPageShapes());

          // Filter selectedShapeIds based on shape owner
          if (next.selectedShapeIds.length > 0) {
            newNext.selectedShapeIds = next.selectedShapeIds.filter((shapeId) => {
              const shapeOwner = formattedLookup[shapeId]?.meta?.createdBy;
              return !shapeOwner || shapeOwner === currentUser?.userId;
            });
          }

          if (!isEqual(prev.hoveredShapeId, next.hoveredShapeId)) {
            const hoveredShapeOwner = formattedLookup[next.hoveredShapeId]?.meta?.createdBy;
            if (hoveredShapeOwner !== currentUser?.userId || next.hoveredShapeId?.includes('shape:BG-')) {
              newNext.hoveredShapeId = null;
            }
          }

          return newNext;
        }

        // Get viewport dimensions and bounds
        let viewportWidth;
        let viewportHeight;

        if (isPresenterRef.current) {
          const viewportPageBounds = editor?.getViewportPageBounds();
          viewportWidth = viewportPageBounds?.w;
          viewportHeight = viewportPageBounds?.h;
        } else {
          viewportWidth = currentPresentationPageRef.current?.scaledViewBoxWidth;
          viewportHeight = currentPresentationPageRef.current?.scaledViewBoxHeight;
        }

        const presentationWidthLocal = currentPresentationPageRef.current?.scaledWidth || 0;
        const presentationHeightLocal = currentPresentationPageRef.current?.scaledHeight || 0;

        // Adjust camera position to ensure it stays within bounds
        // Create boundaries that prevent panning at 100% zoom (fit-to-height level)
        // Allow panning only when users zoom in beyond the default level
        const panned = next?.id?.includes('camera') && (prev.x !== next.x || prev.y !== next.y);
        const zoomed = next?.id?.includes('camera') && (prev.z !== next.z);

        // Only disable boundaries during active zooming (when zoom is changing)
        // Keep centering logic working at 100% zoom to prevent background shape shift
        // if (panned && !currentPresentationPageRef.current?.infiniteWhiteboard && isPresenter) {

        //   // Calculate the actual bounds based on the current zoom level
        //   const currentZoom = next.z || prev.z;

        //   // Safety check for valid zoom level
        //   if (currentZoom && currentZoom > 0) {
        //     const scaledViewportWidth = viewportWidth / currentZoom;
        //     const scaledViewportHeight = viewportHeight / currentZoom;

        //     // Ensure viewport dimensions are valid
        //     if (scaledViewportWidth > 0 && scaledViewportHeight > 0) {
        //       // Get the default fit-to-height zoom level
        //       const defaultZoom = calculateZoomValue(
        //         currentPresentationPageRef.current.scaledWidth,
        //         currentPresentationPageRef.current.scaledHeight
        //       );

        //       // If zoom is at or below default level (100%), prevent all panning
        //       if (currentZoom <= defaultZoom) {
        //         // Reset camera to center position when at default zoom
        //         const centerX = -(currentPresentationPageRef.current.scaledWidth - scaledViewportWidth) / 2;
        //         const centerY = -(currentPresentationPageRef.current.scaledHeight - scaledViewportHeight) / 2;

        //         newNext.x = centerX;
        //         newNext.y = centerY;
        //       } else {
        //         // When zoomed in, use dynamic buffer based on zoom level
        //         // Reduce buffer for lower zoom levels, maintain 250% for higher zoom levels
        //         const zoomRatio = currentZoom / defaultZoom;
        //         let bufferMultiplier;

        //         if (zoomRatio < 2.5) {
        //           // For zoom levels below 250%, use smaller proportional buffer
        //           bufferMultiplier = zoomRatio * 0.5; // Buffer equals 50% of zoom ratio (100% zoom = 50% buffer, 200% zoom = 100% buffer)
        //         } else {
        //           // For zoom levels 250% and above, cap buffer at 250%
        //           bufferMultiplier = 2.5;
        //         }

        //         const bufferX = scaledViewportWidth * bufferMultiplier * 0.4; // Reduce left buffer to 40% of calculated value
        //         const bufferY = scaledViewportHeight * bufferMultiplier * 0.3; // Reduce bottom buffer to 30% of calculated value

        //         // Calculate boundaries that encompass the entire whiteboard content
        //         // This ensures users can pan to see all parts of the whiteboard when zoomed in
        //         const maxX = bufferX + 300;
        //         const minX = -(presentationWidthLocal - scaledViewportWidth - 400);
        //         const maxY = bufferY + 200;
        //         const minY = -(presentationHeightLocal - scaledViewportHeight - 400);

        //         // Apply generous boundaries with large tolerance to prevent content loss
        //         // Users should be able to access the complete whiteboard area
        //         if (next.x > maxX + scaledViewportWidth * 1.0) {
        //           newNext.x = maxX + scaledViewportWidth * 1.0;
        //         } else if (next.x < minX - scaledViewportWidth * 1.0) {
        //           newNext.x = minX - scaledViewportWidth * 1.0;
        //         }

        //         if (next.y > maxY + scaledViewportHeight * 1.0) {
        //           newNext.y = maxY + scaledViewportHeight * 1.0;
        //         } else if (next.y < minY - scaledViewportHeight * 1.0) {
        //           newNext.y = minY - scaledViewportHeight * 1.0;
        //         }
        //       }
        //     }
        //   }
        // }

        return newNext;
      };

      // eslint-disable-next-line no-param-reassign
      editor.store.onAfterChange = (prev, next) => {
        if (next.selectedShapeIds && next.selectedShapeIds?.some((id) => id.includes('shape:BG'))) {
          bgSelectedRef.current = true;
        } else if ((next.selectedShapeIds && !next.selectedShapeIds?.some((id) => id.includes('shape:BG')))) {
          bgSelectedRef.current = false;
        }

        if (next.selectedShapeIds && next.selectedShapeIds?.some((id) => id.includes('shape:BOUNDARY'))) {
          boundarySelectedRef.current = true;
        } else if ((next.selectedShapeIds && !next.selectedShapeIds?.some((id) => id.includes('shape:BOUNDARY')))) {
          boundarySelectedRef.current = false;
        }
      };

      if (!isPresenterRef.current && !hasWBAccessRef.current) {
        editor?.setCurrentTool('noop');
      } else {
        if (isPresenterRef.current) {
          editor?.setCurrentTool('hand');
        } else {
          editor?.setCurrentTool('select');
        }

      }
    }

    pollInnerWrapperDimensionsUntilStable(() => {
      adjustCameraOnMount(!isPresenterRef.current);
    });

    // REMOVED ALL ZOOM CONSTRAINTS - Allow complete freedom for presenters
    // updateZoomConstraints(editor);

    // Ensure camera movement is enabled (NO RESTRICTIONS)
    editor.updateInstanceState({
      canMoveCamera: true,
      isReadonly: false
    });

    // REMOVED PERIODIC ZOOM CONSTRAINT ENFORCEMENT
    // const zoomConstraintInterval = setInterval(() => {
    //   if (editor && !editor.isDestroyed) {
    //     enforceZoomConstraints(editor);
    //   } else {
    //     clearInterval(zoomConstraintInterval);
    //   }
    // }, 100); // Check every 100ms

    // Store the interval reference for cleanup
    // editor._zoomConstraintInterval = zoomConstraintInterval;

    // ADDED: Simple wheel event handler to prevent zooming above 400%

    const handleWheel = (event) => {
      // Only handle zoom when Ctrl key is pressed


      // const updateProps = { isReadonly: false, canMoveCamera: isPresenterRef.current || (isModerator && hasWBAccessRef.current) };

      // ALLOW EVERYONE TO MOVE THERE LOCAL CAMERA
      const updateProps = { isReadonly: false, canMoveCamera: true };
      tlEditorRef?.current?.updateInstanceState(updateProps);
      if (!(event.ctrlKey || event.metaKey)) return;
      if (!currentPresentationPageRef.current) return;
      if (!isPresenterRef.current) return;


      // ZOOM WITH MIN 100% AND MAX 400%
      const baseZoom = calculateZoomValue(
        currentPresentationPageRef.current.scaledWidth,
        currentPresentationPageRef.current.scaledHeight
      );

      const currentZoom = editor.getCamera().z;
      // // Calculate the zoom level relative to base zoom
      let currentZoomLevel = currentZoom / baseZoom;

      // // Update zoom level based on wheel direction
      if (event.deltaY < 0) {
        currentZoomLevel = Math.min(currentZoomLevel + 0.25, 4); // Max 400%
      } else {
        currentZoomLevel = Math.max(currentZoomLevel - 0.25, 0.5); // Min 50%
      }

      // // Convert zoom level to percentage and send to backend
      const zoomPercentage = currentZoomLevel * 100;
      isWheelZoomRef.current = true
      // 👉 mark that I zoomed locally
      lastZoomByMeRef.current = true;
      zoomChanger(zoomPercentage);

      // // If trying to zoom out and already at or below 100%, prevent it
      if (event.deltaY > 0 && currentZoom <= 0.5) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      // // If trying to zoom in and already at or above 400%, prevent it
      if (event.deltaY < 0 && currentZoom >= baseZoom * 4) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add wheel event listener to the editor container
    const editorContainer = editor.getContainer();
    if (editorContainer) {
      editorContainer.addEventListener('wheel', handleWheel, { passive: false, capture: true });
      editor._wheelHandler = handleWheel;
      editor._wheelContainer = editorContainer;
    }
  };

  const calculateZoomWithGapValue = (
    localWidth,
    localHeight,
    widthAdjustment = 0,
  ) => {
    const presentationWidthLocal = Math.max(presentationAreaWidth - widthAdjustment, 0);
    // Prioritize height fitting to ensure content fits within presentation height
    const calcedZoom = Math.min(
      presentationAreaHeight / localHeight,  // Prioritize height fitting
      presentationWidthLocal / localWidth,  // Secondary width constraint
    );
    return calcedZoom === 0 || calcedZoom === Infinity || Number.isNaN(calcedZoom)
      ? calculateZoomValue(localWidth, localHeight) // Fallback to no gap base zoom
      : calcedZoom;
  };

  const syncCameraOnPresenterZoom = ({
    tlEditorRef,
    curPageIdRef,
    currentPresentationPageRef,
    zoomValueRef,
    fitToWidthRef,
    initialZoomRef,
  }) => {
    if (
      !tlEditorRef.current ||
      !curPageIdRef.current ||
      !currentPresentationPageRef.current
    ) {
      return;
    }

    let zoomLevelForReset;
    if (fitToWidthRef.current || !initialZoomRef.current) {
      zoomLevelForReset = calculateZoomValue(
        currentPresentationPageRef.current.scaledWidth,
        currentPresentationPageRef.current.scaledHeight
      );
    } else {
      zoomLevelForReset = initialZoomRef.current;
    }

    const { containerWidth, innerWrapperWidth, widthGap } = getContainerDimensions();

    if (widthGap > 0) {
      zoomLevelForReset = calculateZoomWithGapValue(
        currentPresentationPageRef.current.scaledWidth,
        currentPresentationPageRef.current.scaledHeight,
        widthGap
      );
    }

    const zoomCamera = (zoomLevelForReset * zoomValueRef.current) / HUNDRED_PERCENT;
    const slideShape = tlEditorRef.current.getShape(`shape:BG-${curPageIdRef.current}`);
    const camera = tlEditorRef.current.getCamera();
    const viewportScreenBounds = tlEditorRef.current.getViewportScreenBounds();
    const viewportWidth = viewportScreenBounds.width;
    const viewportHeight = viewportScreenBounds.height;
    let newCamera;
    if (slideShape) {
      const prevZoomCamera = camera.z;
      const prevCenteredCameraX =
        -slideShape.x +
        (viewportWidth - slideShape.props.w * prevZoomCamera) / (2 * prevZoomCamera);
      const prevCenteredCameraY =
        -slideShape.y +
        (viewportHeight - slideShape.props.h * prevZoomCamera) / (2 * prevZoomCamera);

      const panningOffsetX = camera.x - prevCenteredCameraX;
      const panningOffsetY = camera.y - prevCenteredCameraY;

      const centeredCameraX =
        -slideShape.x +
        (viewportWidth - slideShape.props.w * zoomCamera) / (2 * zoomCamera);
      const centeredCameraY =
        -slideShape.y +
        (viewportHeight - slideShape.props.h * zoomCamera) / (2 * zoomCamera);

      newCamera = {
        x: centeredCameraX + panningOffsetX,
        y: centeredCameraY + panningOffsetY,
        z: zoomCamera,
      };
    } else {
      newCamera = {
        x: camera.x + ((viewportWidth / 2) / camera.z - (viewportWidth / 2) / zoomCamera),
        y: camera.y + ((viewportHeight / 2) / camera.z - (viewportHeight / 2) / zoomCamera),
        z: zoomCamera,
      };
    }

    // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
    tlEditorRef.current.store.mergeRemoteChanges(() => {
      tlEditorRef.current.setCamera(newCamera, { duration: 175 });
    });
  }

  const syncCameraWithPresentationArea = ({
    tlEditorRef,
    isPresenter,
    currentPresentationPageRef,
    presentationAreaWidth,
    presentationAreaHeight,
    zoomValueRef,
    fitToWidthRef,
    curPageIdRef,
    initialViewBoxWidthRef,
    initialViewBoxHeightRef,
  }) => {
    if (
      !tlEditorRef.current ||
      !currentPresentationPageRef.current ||
      presentationAreaWidth <= 0 ||
      presentationAreaHeight <= 0
    ) {
      return;
    }

    const currentZoom = zoomValueRef.current || HUNDRED_PERCENT;
    const {
      scaledWidth,
      scaledHeight,
      scaledViewBoxWidth,
      scaledViewBoxHeight,
    } = currentPresentationPageRef.current;

    if (scaledWidth <= 0 || scaledHeight <= 0) {
      return;
    }

    const baseZoom = calculateZoomValue(scaledWidth, scaledHeight);

    let adjustedZoom = (baseZoom * currentZoom) / HUNDRED_PERCENT;

    if (isPresenter) {
      const { containerWidth, innerWrapperWidth, widthGap } = getContainerDimensions();

      if (widthGap > 0) {
        const gapZoom = calculateZoomWithGapValue(scaledWidth, scaledHeight, widthGap) || HUNDRED_PERCENT;
        adjustedZoom = (gapZoom * currentZoom) / HUNDRED_PERCENT;
      }

      const camera = tlEditorRef.current.getCamera();
      const formattedPageId = Number(curPageIdRef.current);
      const zoomToApply = adjustedZoom;
      const updatedCurrentCam = {
        ...camera,
        z: adjustedZoom,
      };
      tlEditorRef.current.store.put([updatedCurrentCam]);
    } else {
      const newZoom = calculateZoomValue(
        scaledViewBoxWidth,
        scaledViewBoxHeight
      );
      const camera = tlEditorRef.current.getCamera();
      const formattedPageId = Number(curPageIdRef.current);
      const updatedCurrentCam = {
        ...camera,
        z: newZoom,
      };
      tlEditorRef.current.store.put([updatedCurrentCam]);
    }
  }

  useMouseEvents(
    {
      whiteboardRef, tlEditorRef, initialZoomRef, isPresenterRef,
    },
    {
      hasWBAccess: hasWBAccessRef.current,
      whiteboardToolbarAutoHide,
      animations,
      publishCursorUpdate,
      whiteboardId: whiteboardIdRef.current,
      cursorPosition,
      updateCursorPosition,
      toggleToolsAnimations,
      currentPresentationPage,
      zoomChanger,
      setIsMouseDown,
      isInfiniteWhiteboard,
      presentationAreaWidth,
      presentationAreaHeight,
    },
  );

  React.useEffect(() => {
    const handleArrowPress = (event) => {
      const currPageNum = parseInt(curPageIdRef.current, 10);
      const shapeSelected = tlEditorRef.current.getSelectedShapes()?.length > 0;
      const changeSlide = (direction) => {
        if (!currentPresentationPage) return;
        const newSlideNum = currPageNum + direction;
        const outOfBounds = direction > 0
          ? newSlideNum > currentPresentationPage?.totalPages
          : newSlideNum < 1;

        if (outOfBounds) return;

        skipToSlide(newSlideNum);
        zoomChanger(HUNDRED_PERCENT);
        zoomSlide(HUNDRED_PERCENT, HUNDRED_PERCENT, 0, 0);
      };

      if (!shapeSelected) {
        if (event.keyCode === KEY_CODES.ARROW_RIGHT) {
          changeSlide(1); // Move to the next slide
        } else if (event.keyCode === KEY_CODES.ARROW_LEFT) {
          changeSlide(-1); // Move to the previous slide
        }
      }
    };

    const handleKeyDown2 = (event) => {
      if (
        (event.keyCode === KEY_CODES.ARROW_RIGHT
          || event.keyCode === KEY_CODES.ARROW_LEFT)
        && isPresenterRef.current
      ) {
        handleArrowPress(event);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === ' ') {
        if (previousTool.current) {
          tlEditorRef.current?.setCurrentTool(previousTool.current);
          previousTool.current = null;
        }
      }
    };

    whiteboardRef.current?.addEventListener('keydown', handleKeyDown2, {
      capture: true,
    });
    whiteboardRef.current?.addEventListener('keyup', handleKeyUp, {
      capture: true,
    });
    return () => {
      whiteboardRef.current?.removeEventListener('keydown', handleKeyDown2);
      whiteboardRef.current?.removeEventListener('keyup', handleKeyUp);
    };
  }, [whiteboardRef.current]);

  React.useEffect(() => {
    zoomValueRef.current = zoomValue;
    setPageZoomMap(prev => ({
      ...prev,
      [curPageIdRef.current]: zoomValue,
    }));

    if (pageChanged) {
      return zoomChanger(pageZoomMap[curPageIdRef.current] || HUNDRED_PERCENT);
    }

    if (
      tlEditorRef.current &&
      curPageIdRef.current &&
      currentPresentationPage &&
      isPresenter
    ) {
      if (!isMounting) {
        if (!isPresenter || !isWheelZoomRef.current) {
          syncCameraOnPresenterZoom({
            tlEditorRef,
            curPageIdRef,
            currentPresentationPageRef,
            zoomValueRef,
            fitToWidthRef,
            initialZoomRef,
          });
        }
        // syncCameraOnPresenterZoom({
        //   tlEditorRef,
        //   curPageIdRef,
        //   currentPresentationPageRef,
        //   zoomValueRef,
        //   fitToWidthRef,
        //   initialZoomRef,
        // });
      }
    }
    // reset flag
    isWheelZoomRef.current = false;
    prevZoomValueRef.current = zoomValue;
  }, [zoomValue, pageChanged, tlEditorRef.current]);

  React.useEffect(() => {
    if (isPresenter) {
      zoomChanger(HUNDRED_PERCENT);
      zoomSlide(HUNDRED_PERCENT, HUNDRED_PERCENT, 0, 0);
    }
  }, [fitToWidth]);

  // Reset first-time centering flag when presentation changes
  React.useEffect(() => {
    if (presentationId) {
      setIsFirstTimeCentering({});
    }
  }, [presentationId]);

  React.useEffect(() => {
    if (
      currentPresentationPageRef.current?.scaledWidth > 0 &&
      currentPresentationPageRef.current?.scaledHeight > 0 &&
      presentationAreaWidth > 0 &&
      presentationAreaHeight > 0
    ) {
      const adjustedPresentationAreaHeight = isPresenterRef.current
        ? presentationAreaHeight - 40
        : presentationAreaHeight;
      const slideAspectRatio =
        currentPresentationPageRef.current.scaledWidth /
        currentPresentationPageRef.current.scaledHeight;
      const presentationAreaAspectRatio =
        presentationAreaWidth / adjustedPresentationAreaHeight;

      let initialZoom;

      // Prioritize height fitting to ensure content fits within presentation height
      // This prevents vertical scrolling while allowing some width flexibility
      initialZoom = Math.min(
        adjustedPresentationAreaHeight / currentPresentationPageRef.current.scaledHeight,  // Prioritize height
        presentationAreaWidth / currentPresentationPageRef.current.scaledWidth,            // Secondary width constraint
      );

      initialZoomRef.current = initialZoom;
      prevZoomValueRef.current = zoomValue;

      // REMOVED ZOOM CONSTRAINTS - Allow complete freedom for presenters
      // if (tlEditorRef.current) {
      //   updateZoomConstraints(tlEditorRef.current);
      // }
    }
  }, [presentationAreaWidth, presentationAreaHeight, presentationWidth, presentationHeight, isPresenter, presentationId, fitToWidth]);

  React.useEffect(() => {
    if (isMountedPollingFrameRef.current !== null) {
      cancelAnimationFrame(isMountedPollingFrameRef.current)
    }
    isMountedPollingFrameRef.current = requestAnimationFrame(() => {
      pollUntilMounted(() => {
        if (innerWrapperPollingFrameRef.current !== null) {
          cancelAnimationFrame(innerWrapperPollingFrameRef.current);
        }
        innerWrapperPollingFrameRef.current = requestAnimationFrame(() => {
          pollInnerWrapperDimensionsUntilStable(() => {
            syncCameraWithPresentationArea({
              tlEditorRef,
              isPresenter,
              currentPresentationPageRef,
              presentationAreaWidth,
              presentationAreaHeight,
              zoomValueRef,
              fitToWidthRef,
              curPageIdRef,
              initialViewBoxWidthRef,
              initialViewBoxHeightRef,
            });
          }, {
            maxTries: 120,
            stabilityFrames: 35,
          }, innerWrapperPollingFrameRef);
        });
      }, () => {
        logger.warn(
          { logCode: 'pollUntilMounted' },
          `Failed to wait for component to be mounted`,
        );
      }, isMountedPollingFrameRef);
    });
  }, [presentationHeight, presentationWidth, curPageId, presentationId]);

  React.useEffect(() => {

    // !isPresenter
    // 👉 Skip if the last zoom was by me
    // if (lastZoomByMeRef.current) {
    //   lastZoomByMeRef.current = false; // reset flag
    //   return;
    // }

    if (
      !isPresenter &&
      tlEditorRef.current
      && initialViewBoxWidthRef.current
      && initialViewBoxHeightRef.current
      && currentPresentationPage
    ) {
      const newZoom = calculateZoomValue(
        currentPresentationPage.scaledViewBoxWidth,
        currentPresentationPage.scaledViewBoxHeight,
      );

      const adjustedXPos = currentPresentationPage.xOffset;
      const adjustedYPos = currentPresentationPage.yOffset;
      // Check against current camera before applying
      const camera = tlEditorRef.current.getCamera();
      if (
        camera.z === newZoom &&
        camera.x === currentPresentationPage?.xOffset &&
        camera.y === currentPresentationPage?.yOffset
      ) {
        return; // avoid unnecessary reset
      }
      setCamera(
        newZoom,
        adjustedXPos,
        adjustedYPos,
      );
    }

  }, [currentPresentationPage, isPresenter]);


  React.useEffect(() => {
    if (tlEditorRef.current) {
      const useElement = document.querySelector('.tl-cursor use');
      if (useElement && !isMultiUserActive && !isPresenter) {
        useElement.setAttribute('href', '#redPointer');
      } else if (useElement) {
        useElement.setAttribute('href', '#cursor');
      }

      const idsToRemove = [];

      // Get all presence records from the store
      const allRecords = tlEditorRef.current.store.allRecords();
      const presenceRecords = allRecords.filter((record) => record.id.startsWith('instance_presence:'));

      // Check if any presence records correspond to users not in whiteboardWriters
      presenceRecords.forEach((record) => {
        const userId = record.userId.split('instance_presence:')[1];
        const hasAccessToWhiteboard = whiteboardWriters.some((writer) => writer.userId === userId);

        if (!hasAccessToWhiteboard) {
          idsToRemove.push(record.id);
        }
      });

      const updatedPresences = otherCursors
        .map(({
          userId, user, xPercent, yPercent,
        }) => {
          const { presenter, name } = user;
          const id = InstancePresenceRecordType.createId(userId);
          const active = xPercent !== -1 && yPercent !== -1;
          // if cursor is not active remove it from tldraw store
          if (
            !active
            || (hideViewersCursor
              && user.role === 'VIEWER'
              && !currentUser?.presenter)
            || (!presenter && !isMultiUserActive)
          ) {
            idsToRemove.push(id);
            return null;
          }

          const cursor = {
            x: xPercent,
            y: yPercent,
            type: 'default',
            rotation: 0,
          };
          const color = presenter ? '#FF0000' : '#70DB70';
          // const cursorName = presenter ? 'Presenter' : name;
          const cursorName = normalizePlusString(name);
          const c = {
            ...InstancePresenceRecordType.create({
              id,
              currentPageId: `page:${curPageIdRef.current}`,
              userId,
              userName: cursorName,
              cursor,
              color,
            }),
            lastActivityTimestamp: Date.now(),
          };

          return c;
        })
        .filter((cursor) => cursor && cursor.userId !== currentUser?.userId);

      if (idsToRemove.length) {
        tlEditorRef.current?.store.remove(idsToRemove);
      }

      // If there are any updated presences, put them all in the store
      if (updatedPresences.length) {
        tlEditorRef.current?.store.put(updatedPresences);
      }
    }
  }, [otherCursors, whiteboardWriters]);

  const createPage = (currentPageId) => [
    {
      meta: {},
      id: currentPageId,
      name: `Slide ${currentPageId?.split(':')[1]}`,
      index: 'a1',
      typeName: 'page',
    },
  ];

  const createCameras = (pageId, tlZ) => {
    const cameras = [];
    const MIN_PAGE_ID = 1;
    const totalPages = currentPresentationPageRef.current?.totalPages || 1;

    if (pageId > MIN_PAGE_ID) {
      cameras.push(createCamera(pageId - 1, tlZ));
    }

    cameras.push(createCamera(pageId, tlZ));

    if (pageId < totalPages) {
      cameras.push(createCamera(pageId + 1, tlZ));
    }

    return cameras;
  };

  const cleanupStore = (currentPageId) => {
    const allRecords = tlEditorRef.current.store.allRecords();
    const shapeIdsToRemove = allRecords
      .filter((record) => {
        return record.typeName === 'shape' && record.parentId;
      })
      .filter((record) => {
        return record?.meta?.presentationId !== presentationIdRef.current || !record?.meta?.presentationId;
      })
      .map((shape) => shape.id);

    if (shapeIdsToRemove.length > 0) {
      tlEditorRef.current?.store.remove([...shapeIdsToRemove]);
    }
  };

  const updateStore = (pages, cameras) => {
    tlEditorRef.current.store.put(pages);
    tlEditorRef.current.store.put(cameras);
    tlEditorRef.current.store.put(assets);
    tlEditorRef.current.store.put(bgShape);
    tlEditorRef.current.store.put(rectShape);
  };

  const finalizeStore = () => {
    tlEditorRef.current.history.clear();
  };

  const toggleToolbarIfNeeded = () => {
    if (whiteboardToolbarAutoHide && toggleToolsAnimations) {
      toggleToolsAnimations('fade-in', 'fade-out', '0s', hasWBAccessRef.current || isPresenterRef.current);
    }
  };

  const resetSlideState = () => {
    slideChanged.current = false;
    slideNext.current = null;
  };

  const createSvgOfCurrentSlide = async (slideNum) => {
    const darkThemeState = AppService.isDarkThemeEnabled();
    AppService.setDarkTheme(false);

    const tldrawAPI = tlEditorRef.current

    try {
      const shapes = tldrawAPI.getCurrentPageShapes();
      const backgroundShape = shapes.find((s) => s.id === `shape:BG-${slideNum}`);
      const pollShape = shapes.find((shape) => shape.type === 'poll');
      const cleanShapes = shapes.filter(
        (s) => !s.id.startsWith('shape:BOUNDARY') && s.type !== 'poll'
      );
      const svgElem = await tldrawAPI.getSvg(cleanShapes.map(s => s.id));

      // Convert to PNG
      let width = svgElem?.width?.baseVal?.value ?? window.screen.width;
      let height = svgElem?.height?.baseVal?.value ?? window.screen.height;


      const MAX_SIZE = 4000;

      // ✅ If either dimension exceeds 5000, cap both to 5000
      if (width > MAX_SIZE / 2 || height > MAX_SIZE / 2) {
        svgElem.setAttribute('width', MAX_SIZE);
        svgElem.setAttribute('height', MAX_SIZE);
        svgElem.setAttribute('viewBox', `${-MAX_SIZE / 2} ${-MAX_SIZE / 2} ${MAX_SIZE} ${MAX_SIZE}`);
        width = MAX_SIZE;
        height = MAX_SIZE;
      }

      const pngData = await toPng(svgElem, { width, height, backgroundColor: '#FFF' });

      // restore theme
      AppService.setDarkTheme(darkThemeState);

      return {
        slideNum,
        imageData: pngData,
        presentationId,
        presentationName,
        // courseId:'691417df3f9a24040803530e',
        // courseSessionId:'69141a923f9a24040803603f'
        courseId: courseDetails?.course?.courseDetail?.courseId,
        courseSessionId: courseDetails?.course?.courseDetail?.courseSessionId
      };

    } catch (e) {
      console.error("ERROR IN CREATE SVG:", e);
      AppService.setDarkTheme(darkThemeState);
      return null;
    }
  };
  const lastCapturedPageRef = useRef(curPageId ?? 1);
  // 1️⃣ Function to capture current slide and update array
  const captureCurrentSlide = async (currentSlideNum = lastCapturedPageRef?.current, isCron = false) => {
    if (!isCron) {
      setIsPdfLoading(true);
    }
    const result = await createSvgOfCurrentSlide(currentSlideNum);

    if (!result) return null;


    const slidesPayload = result
    return insertSlideData(slidesPayload)
      .then(() => {

        // Success
      })
      .catch((error) => {
        console.log({ error });
      }).finally(() => {
        lastCapturedPageRef.current = curPageId;
        if (!isCron) {
          setIsPdfLoading(false);
        }
      });

  };


  React.useEffect(() => {
    const formattedPageId = parseInt(curPageIdRef.current, 10);
    if (tlEditorRef.current && formattedPageId !== 0) {
      // Capture the current zoom level before the store operations
      const currentZoom = tlEditorRef.current.getCamera()?.z || 1;



      tlEditorRef.current.store.mergeRemoteChanges(() => {
        tlEditorRef.current.batch(async () => {
          const currentPageId = `page:${formattedPageId}`;
          const tlZ = tlEditorRef.current.getCamera()?.z;
          const cameras = [];
          const pages = [];
          const currPageExists = tlEditorRef.current?.getPage(currentPageId);
          const existingPageId = editor.getCurrentPageId();



          const pageNumber = Number(existingPageId.replace("page:", ""))


          if (!prepareLesson) {
            await captureCurrentSlide(pageNumber)
          }
          if (!currPageExists) {
            const currentPage = createPage(currentPageId);
            pages.push(...currentPage);
          }
          const allRecords = tlEditorRef.current.store.allRecords();
          const cameraRecords = allRecords.filter(record => record.typeName === "camera" && record.id?.split(':').pop() == formattedPageId);
          if (cameraRecords?.length < 1) {
            cameras.push(createCamera(formattedPageId, tlZ));
          }
          cleanupStore(currentPageId);
          updateStore(pages, cameras);
          tlEditorRef.current.setCurrentPage(currentPageId);


          const slideShape = tlEditorRef.current.getShape(`shape:BG-${formattedPageId}`);
          if (slideShape) {
            const viewportScreenBounds = tlEditorRef.current.getViewportScreenBounds();
            const viewportWidth = viewportScreenBounds.width;
            const viewportHeight = viewportScreenBounds.height;

            // Calculate proper center position accounting for background shape position
            const centeredCameraX = -slideShape.x + (viewportWidth - slideShape.props.w * currentZoom) / (2 * currentZoom);
            const centeredCameraY = -slideShape.y + (viewportHeight - slideShape.props.h * currentZoom) / (2 * currentZoom);

            // Check if this is the first time centering this slide
            const slideKey = `${presentationIdRef.current}-${formattedPageId}`;
            const isFirstTime = !isFirstTimeCentering[slideKey];

            if (isFirstTime) {
              // First time centering - set camera to center and sync with viewers
              // Use mergeRemoteChanges to prevent triggering store listeners and avoid infinite loops
              // This is especially important since this code runs inside an async batch callback
              tlEditorRef.current.store.mergeRemoteChanges(() => {
                tlEditorRef.current.setCamera({
                  x: centeredCameraX,
                  y: centeredCameraY,
                  z: currentZoom
                }, { duration: 0 });
              });

              // Mark as first time done
              setIsFirstTimeCentering(prev => ({ ...prev, [slideKey]: true }));


              // Synchronize camera centering with viewers through zoomSlide
              if (isPresenterRef.current && currentPresentationPageRef.current && currentPresentationPageRef.current.scaledWidth && currentPresentationPageRef.current.scaledHeight) {
                // Use setTimeout to ensure store operations are complete before calling zoomSlide
                setTimeout(() => {
                  try {
                    const viewedRegionW = SlideCalcUtil.calcViewedRegionWidth(
                      tlEditorRef.current.getViewportPageBounds()?.w || viewportWidth / currentZoom,
                      currentPresentationPageRef.current.scaledWidth,
                    );
                    const viewedRegionH = SlideCalcUtil.calcViewedRegionHeight(
                      tlEditorRef.current.getViewportPageBounds()?.h || viewportHeight / currentZoom,
                      currentPresentationPageRef.current.scaledHeight,
                    );

                    // console.log('First time centering - synchronizing with viewers:', {
                    //   slideId: formattedPageId,
                    //   viewedRegionW,
                    //   viewedRegionH,
                    //   camera: { x: centeredCameraX, y: centeredCameraY, z: currentZoom },
                    //   isPresenter: isPresenterRef.current,
                    //   hasPresentationPage: !!currentPresentationPageRef.current
                    // });
                    zoomSlide(
                      viewedRegionW, viewedRegionH, centeredCameraX, centeredCameraY,
                      currentPresentationPageRef.current,
                    );
                  } catch (error) {
                    console.warn('Failed to synchronize first-time camera centering:', error);
                  }
                }, 50); // Small delay to ensure store is ready
              }
            } else {
              // Not first time - keep presenter's current camera position (don't force center)
              // console.log('Subsequent centering - keeping presenter current position (no centering, no viewer sync):', {
              //   slideId: formattedPageId,
              //   currentCamera: tlEditorRef.current.getCamera(),
              //   isFirstTime: false
              // });
              // Don't call setCamera - let presenter keep their current position
            }





            // console.log('Camera centered on slide (first visit or no stored state):', {
            //   slideId: formattedPageId,
            //   camera: { x: centeredCameraX, y: centeredCameraY, z: currentZoom },
            //   backgroundShape: { x: slideShape.x, y: slideShape.y, w: slideShape.props.w, h: slideShape.props.h }
            // });
          } else {
            // Fallback: if background shape not available, use default centering
            console.warn('Background shape not available for slide:', formattedPageId);
          }


          finalizeStore();
        });
      });



      toggleToolbarIfNeeded();
      resetSlideState();
    }
  }, [curPageId]);

  React.useEffect(() => {
    const handleEndMeetingReq = async (event) => {
      if (event.detail?.isEndReq) {
        const formattedPageId = parseInt(curPageIdRef.current, 10);
        const currentSlideNum = formattedPageId
        if (!prepareLesson) {
          await captureCurrentSlide(currentSlideNum)
        }
        // Now tell endMeeting() that saving is done
        window.dispatchEvent(
          new CustomEvent("end-meeting-done", {
            detail: { isEndDone: true }
          })
        );
      }
    };

    window.addEventListener("end-meeting-req", handleEndMeetingReq);

    return () => {
      window.removeEventListener("end-meeting-req", handleEndMeetingReq);
    };
  }, []);

  // React.useEffect(() => {
  //   if (prepareLesson) return; // 👈 don't start interval

  //   const FIVE_MINUTES = 2 * 60 * 1000;

  //   const interval = setInterval(() => {
  //     const currentSlideNum = parseInt(curPageIdRef.current, 10);
  //     captureCurrentSlide(currentSlideNum, true);
  //   }, FIVE_MINUTES);

  //   return () => clearInterval(interval);
  // }, [prepareLesson]);  // 👈 re-run effect only when value changes

  React.useEffect(() => {
    if (prepareLesson) return; // don't start interval when preparing

    const FIVE_MINUTES = 2 * 60 * 1000;

    const interval = setInterval(() => {
      const currentSlideNum = parseInt(curPageIdRef.current, 10);
      captureCurrentSlide(currentSlideNum, true);
    }, FIVE_MINUTES);

    return () => clearInterval(interval);
  }, [
    prepareLesson,
    presentationId,     // 👈 interval resets when presentation changes
    curPageIdRef.current // 👈 OR when current page changes
  ]);


  React.useEffect(() => {
    const handlePresentationChange = async (event) => {
      const { isChangeReq, presentationId } = event.detail;

      // Do nothing if prepareLesson is ON


      if (isChangeReq) {

        const currentSlideNum = parseInt(lastCapturedPageRef.current, 10);
        if (!prepareLesson) {
          await captureCurrentSlide(currentSlideNum);
        }


        window.dispatchEvent(
          new CustomEvent("presentation-change-done", {
            detail: {
              isChanged: true,
              presentationId,
              currentSlideNum
            }
          })
        );
      }
    };

    window.addEventListener("presentation-change", handlePresentationChange);

    return () => {
      window.removeEventListener(
        "presentation-change",
        handlePresentationChange
      );
    };
  }, [prepareLesson]); // 👈 ensures latest value is used

  React.useEffect(() => {
    setTldrawIsMounting(true);


    return () => {
      isMountedRef.current = false;
      localStorage.removeItem('initialViewBoxWidth');
      localStorage.removeItem('initialViewBoxHeight');
      localStorage.removeItem('pageZoomMap');

      // Clean up global editor reference
      window.tlEditorRef = null;

      // REMOVED ZOOM CONSTRAINT CLEANUP - No more constraints
      // if (tlEditorRef.current && tlEditorRef.current._zoomConstraintInterval) {
      //   clearInterval(tlEditorRef.current._zoomConstraintInterval);
      // }

      // ADDED: Clean up wheel event handler for 400% zoom limit
      // if (tlEditorRef.current && tlEditorRef.current._wheelHandler && tlEditorRef.current._wheelContainer) {
      //   tlEditorRef.current._wheelContainer.removeEventListener('wheel', tlEditorRef.current._wheelHandler, { capture: true });
      // }


      // ADDED: Clean up touch event handlers for pinch zoom


      if (mountedTimeoutIdRef.current) {
        clearTimeout(mountedTimeoutIdRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (isMounting) {
      setIsMounting(false);
      /// brings presentation toolbar back
      setTldrawIsMounting(false);
    }
  }, [tlEditorRef?.current?.camera, presentationAreaWidth, presentationAreaHeight, presentationId]);

  React.useEffect(() => {
    const bbbMultiUserTools = getFromUserSettings(
      'bbb_multi_user_tools',
      meetingClientSettingsInitialValues.public.whiteboard.toolbar.multiUserTools,
    );
    const allElements = document.querySelectorAll('[data-testid^="tools."]');
    const actionsElement = document.querySelector('[data-testid="main.action-menu"]');

    if (bbbMultiUserTools.length >= 1 && !isModerator) {
      allElements.forEach((element) => {
        const toolName = element.getAttribute('data-testid').split('.')[1];



        if (!bbbMultiUserTools.includes(toolName)) {
          // eslint-disable-next-line no-param-reassign
          element.style.display = 'none';
        }
      });

      if (actionsElement) {
        if (!bbbMultiUserTools.includes('actions')) {
          actionsElement.style.display = 'none';
        }
      }
    }
    // TODO: we should add the dependency  list in [] parameter here
    // so this is not run on every render
  });

  React.useEffect(() => {
    const bbbPresenterTools = getFromUserSettings(
      'bbb_presenter_tools',
      meetingClientSettingsInitialValues.public.whiteboard.toolbar.presenterTools,
    );
    const allElements = document.querySelectorAll('[data-testid^="tools."]');
    const actionsElement = document.querySelector('[data-testid="main.action-menu"]');

    if (bbbPresenterTools.length >= 1 && isPresenter) {
      allElements.forEach((element) => {
        const toolName = element.getAttribute('data-testid').split('.')[1];
        if (!bbbPresenterTools.includes(toolName)) {
          // eslint-disable-next-line no-param-reassign
          element.style.display = 'none';
        }
      });

      if (actionsElement) {
        if (!bbbPresenterTools.includes('actions')) {
          actionsElement.style.display = 'none';
        }
      }
    }
    // TODO: we should add the dependency  list in [] parameter here
    // so this is not run on every render
  });

  React.useEffect(() => {
    const bbbMultiUserPenOnly = getFromUserSettings(
      'bbb_multi_user_pen_only',
      false,
    );
    const allElements = document.querySelectorAll('[data-testid^="tools."]');
    const actionsElement = document.querySelector('[data-testid="main.action-menu"]');

    if (bbbMultiUserPenOnly && !isModerator && !isPresenter) {
      allElements.forEach((element) => {
        const toolName = element.getAttribute('data-testid').split('.')[1];

        const displayStyle = toolName !== 'draw' ? 'none' : 'flex';
        // eslint-disable-next-line no-param-reassign
        element.style.display = displayStyle;
      });

      if (actionsElement) {
        actionsElement.style.display = 'none';
      }
    }
    // TODO: we should add the dependency  list in [] parameter here
    // so this is not run on every render
  });

  React.useEffect(() => {
    const baseName =
      window.meetingClientSettings.public.app.cdn
      + window.meetingClientSettings.public.app.basename;
    const makeCursorUrl = (filename) => `${baseName}/resources/images/whiteboard-cursor/${filename}`;

    const TOOL_CURSORS = {
      draw: `url('${makeCursorUrl('pencil.png')}') 2 22, default`,
      line: `url('${makeCursorUrl('line.png')}'), default`,
      text: `url('${makeCursorUrl('text.png')}'), default`,
      note: `url('${makeCursorUrl('square.png')}'), default`,
      pan: `url('${makeCursorUrl('pan.png')}'), default`,
      // eraser: `url('${makeCursorUrl('eraser.svg')}'), default`,
    };

    const currentTool = tlEditorRef.current?.getCurrentToolId();
    const newCursor = hasWBAccessRef.current || currentUser?.presenter ? TOOL_CURSORS[currentTool] || '' : 'inherit';
    setCursorType(newCursor);
  }, [tlEditorRef.current?.getCurrentToolId()]);


  const hasDeletedRef = React.useRef(false);

  const lastZoomByMeRef = useRef(false);
  // React.useEffect(() => {
  //   if (hasDeletedRef.current || !tlEditorRef.current) return;

  //   const deletableIds = tlEditorRef.current.getCurrentPageShapes()
  //     .map(shape => {
  //       if (currentUser?.presenter || shape?.meta?.createdBy === currentUser?.userId) {
  //         return shape.id;
  //       }
  //       return '';
  //     })
  //     .filter(id => id.length > 0);

  //   if (deletableIds.length > 0) {
  //     hasDeletedRef.current = true; // prevent future runs
  //     setTimeout(() => {
  //       tlEditorRef.current.deleteShapes(deletableIds);

  //       setTimeout(() => {
  //         tlEditorRef.current.undo();
  //       }, 1000); // Delay before undo

  //     }, 500); // Delay before deletion
  //   }
  // }, [currentUser, tlEditorRef.current]);


  React.useEffect(() => {
    if (hasDeletedRef.current || !tlEditorRef.current) return;

    const editor = tlEditorRef.current;

    const shapesToReRegister = editor.getCurrentPageShapes()
      .map(shape => {
        if (currentUser?.presenter || shape?.meta?.createdBy === currentUser?.userId) {
          return shape;
        }
        return '';
      })

    if (shapesToReRegister.length > 0) {
      hasDeletedRef.current = true; // prevent future runs
      setTimeout(() => {
        shapesToReRegister.forEach((shape) => {
          // re-register the shape by putting it back in the store

          persistShapeWrapper(
            shape,
            whiteboardIdRef.current,
            isModeratorRef.current,
          );
          // editor.store.put({
          //   ...shape,
          //   id: shape.id, // keep same id so it's "re-registered"
          // });
        });
      }, 500);
    }
  }, [currentUser, tlEditorRef.current]);


  const { data: presentationData } = useDeduplicatedSubscription(PRESENTATIONS_SUBSCRIPTION);
  const [presentations, setPresentations] = useState(presentationData?.pres_presentation || [])
  const [toUploadCount, setToUploadCount] = useState(0)
  const [disableActions, setDisableActions] = useState(false)
  const [presentationSetCurrent] = useMutation(PRESENTATION_SET_CURRENT);
  const [presentationRemove] = useMutation(PRESENTATION_REMOVE);
  const setPresentation = async (presentationId) => {
    presentationSetCurrent({ variables: { presentationId } });
  };

  const handleCurrentChange = async (id) => {
    setPresentations((prevPresentations) => {
      if (disableActions || prevPresentations.length === 0) return prevPresentations
      const currentIndex = prevPresentations.findIndex((p) => p.current)
      const newCurrentIndex = prevPresentations.findIndex((p) => p.presentationId === id)
      const commands = {}
      if (currentIndex !== -1) {
        commands[currentIndex] = {
          $apply: (presentation) => ({ ...presentation, current: false }),
        }
      }
      if (newCurrentIndex !== -1) {
        commands[newCurrentIndex] = {
          $apply: (presentation) => ({ ...presentation, current: true }),
        }
      }
      return update(prevPresentations, commands)
    })
  }


  // const components = {
  //   InFrontOfTheCanvas: () => {
  //     const editor = useEditor()
  //     const { width: browserWidth } = layoutSelectInput((i) => i.browser);
  //     const info = useValue('selection bounds', () => {
  //       const pageBounds = editor.getSelectionPageBounds()
  //       if (!pageBounds) return null

  //       const topLeftScreen = editor.pageToScreen({ x: pageBounds.x, y: pageBounds.y })
  //       return {
  //         x: topLeftScreen.x,
  //         y: topLeftScreen.y,
  //         // width: pageBounds.width,
  //         // height: pageBounds.height,
  //         width: pageBounds.width * editor.getZoomLevel(),
  //         height: pageBounds.height * editor.getZoomLevel(),
  //       }
  //     }, [editor])

  //     // const selectedShape = editor.getSelectedShapes()[0]
  //     // const isLocked = selectedShape?.isLocked

  //     // if (!info || !selectedShape || !isLocked || !isDesktop()) return null
  //     // return (
  //     //   <div
  //     //     style={{
  //     //       position: 'absolute',
  //     //       top: 0,
  //     //       left: 0,
  //     //       transformOrigin: 'top left',
  //     //       transform: `translate(${info.x}px, ${info.y}px)`,
  //     //       pointerEvents: 'all',
  //     //       width: info?.width
  //     //     }}
  //     //     onPointerDown={stopEventPropagation}
  //     //   >
  //     //     <button
  //     //       onClick={() => {
  //     //         editor.updateShapes([
  //     //           {
  //     //             id: selectedShape.id,
  //     //             type: selectedShape.type,
  //     //             props: selectedShape.props,
  //     //             isLocked: false,
  //     //           },
  //     //         ])
  //     //       }}
  //     //       style={{
  //     //         position: 'absolute',
  //     //         left: (info.width / 2) - 10,
  //     //         top: '-90px',
  //     //         width: 32,
  //     //         height: 32,
  //     //         borderRadius: '4px',
  //     //         // borderRadius: '50%',
  //     //         background: '#00265d',
  //     //         // boxShadow: '0 0 4px rgba(0,0,0,0.2)',
  //     //         border: '1px solid #ccc',
  //     //         display: 'flex',
  //     //         justifyContent: 'center',
  //     //         alignItems: 'center',
  //     //         cursor: 'pointer',
  //     //         fontSize: 16,
  //     //       }}
  //     //       title="Unlock shape"
  //     //     >
  //     //       {/* 🔒 */}
  //     //       <svg width="32px" height="32px" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
  //     //         <path d="M13.5 16.5854C13.5 17.4138 12.8284 18.0854 12 18.0854C11.1716 18.0854 10.5 17.4138 10.5 16.5854C10.5 15.7569 11.1716 15.0854 12 15.0854C12.8284 15.0854 13.5 15.7569 13.5 16.5854Z" fill="#fff" />
  //     //         <path fill-rule="evenodd" clip-rule="evenodd" d="M5.94209 10.0005C5.93921 9.87333 5.9375 9.73733 5.9375 9.59375C5.9375 8.70739 6.00254 7.50382 6.27381 6.28307C6.54278 5.07271 7.03242 3.76302 7.94009 2.74189C8.8791 1.6855 10.2132 1 12 1C13.7868 1 15.1209 1.6855 16.0599 2.74189C16.9676 3.76302 17.4572 5.07271 17.7262 6.28307C17.9975 7.50382 18.0625 8.70739 18.0625 9.59375C18.0625 9.73733 18.0608 9.87333 18.0579 10.0005C19.688 10.0314 21 11.3625 21 13V20C21 21.6569 19.6569 23 18 23H6C4.34315 23 3 21.6569 3 20V13C3 11.3625 4.31196 10.0314 5.94209 10.0005ZM16.0573 10C16.0605 9.87465 16.0625 9.73868 16.0625 9.59375C16.0625 8.79261 16.0025 7.74618 15.7738 6.71693C15.5428 5.67729 15.1574 4.73698 14.5651 4.07061C14.0041 3.4395 13.2132 3 12 3C10.7868 3 9.9959 3.4395 9.43491 4.07061C8.84258 4.73698 8.45722 5.67729 8.22619 6.71693C7.99747 7.74618 7.9375 8.79261 7.9375 9.59375C7.9375 9.73868 7.93946 9.87465 7.94265 10H16.0573ZM19 13C19 12.4477 18.5523 12 18 12H6C5.44772 12 5 12.4477 5 13V20C5 20.5523 5.44772 21 6 21H18C18.5523 21 19 20.5523 19 20V13Z" fill="#fff" />
  //     //       </svg>
  //     //     </button>
  //     //   </div>
  //     // )
  //   },
  // }

  React.useEffect(() => {
    currentPresentationPageRef.current = currentPresentationPage;

    // REMOVED ZOOM CONSTRAINTS - Allow complete freedom for presenters [SECOND]
    // NOTE: Removed setCamera call that was causing potential infinite loops
    // The setCamera({ ...currentCamera }) was setting camera to the same value,
    // which could trigger store updates and cause Maximum call stack exceeded errors
    // if (tlEditorRef.current && currentPresentationPage) {
    //   updateZoomConstraints(tlEditorRef.current);
    // }

  }, [currentPresentationPage]);
  const UnlockButton = ({ editor }) => {
    const selectedShape = editor.getSelectedShapes()[0]
    if (!selectedShape?.isLocked || (selectedShape?.id?.startsWith('shape:BG') || selectedShape?.id?.startsWith('shape:BOUNDARY'))) return null
    const bounds = editor.getSelectionPageBounds()
    if (!bounds) return null

    // Convert page coords → screen coords
    const pos = editor.pageToScreen({
      x: bounds.x + bounds.width / 2,
      y: bounds.y,
    })

    return (
      <button
        style={{
          position: 'absolute',
          top: pos.y - 50, // 50px above the shape
          left: pos.x - 16,
          width: 32,
          height: 32,
          zIndex: 9999,
          background: '#00265d',
          color: '#fff',
          border: '1px solid #ccc',
          borderRadius: 4,
          cursor: 'pointer',
        }}
        onClick={() => {
          editor.updateShapes([
            { id: selectedShape.id, type: selectedShape.type, isLocked: false },
          ])
        }}
      >
        {/* 🔓 */}
        <svg width="18px" height="18px" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.5 16.5854C13.5 17.4138 12.8284 18.0854 12 18.0854C11.1716 18.0854 10.5 17.4138 10.5 16.5854C10.5 15.7569 11.1716 15.0854 12 15.0854C12.8284 15.0854 13.5 15.7569 13.5 16.5854Z" fill="#fff" />
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.94209 10.0005C5.93921 9.87333 5.9375 9.73733 5.9375 9.59375C5.9375 8.70739 6.00254 7.50382 6.27381 6.28307C6.54278 5.07271 7.03242 3.76302 7.94009 2.74189C8.8791 1.6855 10.2132 1 12 1C13.7868 1 15.1209 1.6855 16.0599 2.74189C16.9676 3.76302 17.4572 5.07271 17.7262 6.28307C17.9975 7.50382 18.0625 8.70739 18.0625 9.59375C18.0625 9.73733 18.0608 9.87333 18.0579 10.0005C19.688 10.0314 21 11.3625 21 13V20C21 21.6569 19.6569 23 18 23H6C4.34315 23 3 21.6569 3 20V13C3 11.3625 4.31196 10.0314 5.94209 10.0005ZM16.0573 10C16.0605 9.87465 16.0625 9.73868 16.0625 9.59375C16.0625 8.79261 16.0025 7.74618 15.7738 6.71693C15.5428 5.67729 15.1574 4.73698 14.5651 4.07061C14.0041 3.4395 13.2132 3 12 3C10.7868 3 9.9959 3.4395 9.43491 4.07061C8.84258 4.73698 8.45722 5.67729 8.22619 6.71693C7.99747 7.74618 7.9375 8.79261 7.9375 9.59375C7.9375 9.73868 7.93946 9.87465 7.94265 10H16.0573ZM19 13C19 12.4477 18.5523 12 18 12H6C5.44772 12 5 12.4477 5 13V20C5 20.5523 5.44772 21 6 21H18C18.5523 21 19 20.5523 19 20V13Z" fill="#fff" />
        </svg>
      </button>
    )
  }
  return (
    <div
      ref={whiteboardRef}
      id="whiteboard-element"
      key={`animations=-${animations}-${whiteboardToolbarAutoHide}-${language}-${presentationId}-${fitToWidth}`}
    >
      <Tldraw
        autoFocus={true}
        key={`tldrawv2-${presentationId}-${animations}`}
        forceMobile
        shapeUtils={shapeUtils}
        hideUi={!(hasWBAccessRef.current || isPresenter)}
        onMount={handleTldrawMount}
        tools={customTools}
        overrides={customUiOverrides}
        // components={components}
        // Enable camera movement by default
        canMoveCamera={true}
      >
        <DropEvents
          setPresentation
          toUploadCount
          setToUploadCount={setToUploadCount}
          handleCurrentChange={handleCurrentChange}
          intl
          isPrepareLesson={prepareLesson}
          captureCurrentSlide={captureCurrentSlide}
          removePresentation
          curPageId={lastCapturedPageRef?.current ?? curPageId}
        />
      </Tldraw>

      {tlEditorRef.current && <UnlockButton editor={tlEditorRef.current} />}
      <Styled.TldrawV2GlobalStyle
        {...{
          hasWBAccess: hasWBAccessRef.current,
          bgSelected: bgSelectedRef.current,
          boundarySelected: boundarySelectedRef.current,
          isPresenter,
          isModerator,
          isRTL,
          isMultiUserActive,
          isToolbarVisible,
          presentationHeight,
          cursorType,
          isDesktop: isDesktop()
        }}
      />
    </div>
  );
});

export default Whiteboard;

Whiteboard.propTypes = {
  isPresenter: PropTypes.bool,
  isPhone: PropTypes.bool,
  removeShapes: PropTypes.func.isRequired,
  persistShapeWrapper: PropTypes.func.isRequired,
  notifyNotAllowedChange: PropTypes.func.isRequired,
  shapes: PropTypes.arrayOf(PropTypes.shape).isRequired,
  assets: PropTypes.arrayOf(PropTypes.shape).isRequired,
  currentUser: PropTypes.shape({
    userId: PropTypes.string.isRequired,
  }),
  whiteboardId: PropTypes.string,
  zoomSlide: PropTypes.func.isRequired,
  curPageNum: PropTypes.number.isRequired,
  presentationWidth: PropTypes.number.isRequired,
  presentationHeight: PropTypes.number.isRequired,
  zoomChanger: PropTypes.func.isRequired,
  isRTL: PropTypes.bool.isRequired,
  fitToWidth: PropTypes.bool.isRequired,
  zoomValue: PropTypes.number.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  colorStyle: PropTypes.string.isRequired,
  dashStyle: PropTypes.string.isRequired,
  fillStyle: PropTypes.string.isRequired,
  fontStyle: PropTypes.string.isRequired,
  sizeStyle: PropTypes.string.isRequired,
  presentationAreaHeight: PropTypes.number.isRequired,
  presentationAreaWidth: PropTypes.number.isRequired,
  maxNumberOfAnnotations: PropTypes.number.isRequired,
  darkTheme: PropTypes.bool.isRequired,
  setTldrawIsMounting: PropTypes.func.isRequired,
  presentationId: PropTypes.string,
  setTldrawAPI: PropTypes.func.isRequired,
  isMultiUserActive: PropTypes.bool,
  whiteboardToolbarAutoHide: PropTypes.bool,
  toggleToolsAnimations: PropTypes.func.isRequired,
  animations: PropTypes.bool,
  isToolbarVisible: PropTypes.bool,
  isModerator: PropTypes.bool,
  currentPresentationPage: PropTypes.shape(),
  hasWBAccess: PropTypes.bool,
  bgShape: PropTypes.arrayOf(PropTypes.shape).isRequired,
  publishCursorUpdate: PropTypes.func.isRequired,
  otherCursors: PropTypes.arrayOf(PropTypes.shape).isRequired,
  hideViewersCursor: PropTypes.bool,
  skipToSlide: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  selectedLayout: PropTypes.string.isRequired,
  isInfiniteWhiteboard: PropTypes.bool,
  whiteboardWriters: PropTypes.arrayOf(PropTypes.shape).isRequired,
};
